import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Plus, X, Link2, Trash2, Pencil, Search, AlertTriangle, CheckCircle2, ExternalLink, Landmark, ChevronDown, Vote, Copy, List, CalendarDays, ChevronLeft, ChevronRight, Mail, ShieldCheck, ArrowLeft, Users, UserX, BarChart3, History, UserCircle2, Download, Upload, Printer, FileSpreadsheet, LogOut, ListChecks, Clock } from "lucide-react";
import { Syncer, ApiError, markBackupTaken, restoreBackup, devLogout, fetchData } from "./api.js";
import { buildBackupZip, backupFilename, downloadBlob, readBackupZip, backupBannerLevel } from "./backup.js";
import { STARTER_TASKS } from "./starterTasks.js";
import { printTasks, downloadTable, taskRows, electionRows, peopleRows, auditRows, fileSlug } from "./exports.js";
import SuiteSwitcher from "./SuiteSwitcher.jsx";

// Identity comes from the Election Admin Suite session (see worker/src/session.js): the person's
// name is attached to every change in a task's history, and their role decides whether they see
// the Admin screen. The original artifact had people type their own name instead ("AUTH_MODE
// local"), which was an honor system; there is no typed-in identity any more.

const NAVY = "#1b2a4a";
const GOLD = "#c9a227";
const PAPER = "#f5f6f9";
const RED = "#b22234";
const CARD = "#ffffff";
const INK = "#18213a";
const MUTED = "#56607a";
const BORDER = "#d9dde7";
const BODY_FONT = "'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";
const DISPLAY_FONT = "'Merriweather', Georgia, 'Times New Roman', serif";

const CATEGORIES = [
  { id: "absentee", label: "Absentee Ballots", color: "#2f5d8a" },
  { id: "ballot", label: "Ballot Prep", color: NAVY },
  { id: "comms", label: "Communications", color: "#4a5f8a" },
  { id: "electionday", label: "Election Day Ops", color: "#8a2432" },
  { id: "equipment", label: "Equipment", color: RED },
  { id: "legal", label: "Legal / Compliance", color: "#8a6d1f" },
  { id: "pollworker", label: "Poll Workers", color: "#9a7a12" },
  { id: "postelection", label: "Post-Election", color: "#5c5a70" },
  { id: "voterreg", label: "Voter Registration", color: "#2f6b5a" },
  { id: "other", label: "Other", color: MUTED },
];

const STATUSES = [
  { id: "not_started", label: "Not Started" },
  { id: "in_progress", label: "In Progress" },
  { id: "blocked", label: "Blocked" },
  { id: "completed", label: "Completed" },
];

const catInfo = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[CATEGORIES.length - 1];

function toISODate(d) { return d.toISOString().slice(0, 10); }
function todayISO() { const d = new Date(); d.setHours(0, 0, 0, 0); return toISODate(d); }
function parseISO(s) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
function addDays(dateISO, days) { const d = parseISO(dateISO); d.setDate(d.getDate() + days); return toISODate(d); }
function dayDiff(aISO, bISO) { return Math.round((parseISO(aISO) - parseISO(bISO)) / 86400000); }
function formatNiceDate(iso) {
  return parseISO(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function formatDateTime(isoTimestamp) {
  return new Date(isoTimestamp).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
// 2.1.3: "3:45 PM", for how long a Poll Worker Management sign-in stays confirmed.
function formatTime(isoTimestamp) {
  return new Date(isoTimestamp).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function milestoneToDate(electionDateISO, milestone) {
  const days = milestone.unit === "weeks" ? milestone.amount * 7 : milestone.amount;
  const signedDays = milestone.side === "before" ? -days : days;
  return addDays(electionDateISO, signedDays);
}
function milestoneLabel(milestone) {
  const prefix = milestone.unit === "weeks" ? "W" : "E";
  const sign = milestone.side === "before" ? "-" : "+";
  return `${prefix}${sign}${milestone.amount}`;
}

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }

// Migrates older single-assignee tasks (assignedTo/assignedEmail strings) to the assignees array shape.
function normalizeTask(t) {
  let assignees = t.assignees;
  if (!Array.isArray(assignees)) {
    assignees = [];
    if (t.assignedTo || t.assignedEmail) {
      assignees.push({ id: uid(), name: t.assignedTo || "", email: t.assignedEmail || "" });
    }
  }
  const history = Array.isArray(t.history) ? t.history : [];
  const { assignedTo, assignedEmail, ...rest } = t;
  return { ...rest, assignees, history };
}

// Ensures every assignee is linked to a directory entry (personId), creating directory
// entries for any names/emails that don't have one yet (e.g. from older tasks).
function reconcilePeopleAndTasks(tasksIn, peopleIn) {
  const people = peopleIn.map((p) => ({ ...p }));
  const keyOf = (name, email) => `${(name || "").trim().toLowerCase()}|${(email || "").trim().toLowerCase()}`;
  const byKey = new Map(people.map((p) => [keyOf(p.name, p.email), p]));
  let changed = false;

  const tasks = tasksIn.map((t) => {
    const assignees = (t.assignees || []).map((a) => {
      if (a.personId && people.some((p) => p.id === a.personId)) return a;
      const key = keyOf(a.name, a.email);
      let person = byKey.get(key);
      if (!person && (a.name || a.email)) {
        person = { id: uid(), name: a.name || "", email: a.email || "" };
        people.push(person);
        byKey.set(key, person);
        changed = true;
      }
      if (person && a.personId !== person.id) {
        changed = true;
        return { ...a, personId: person.id };
      }
      return a;
    });
    return assignees === t.assignees ? t : { ...t, assignees };
  });

  return { tasks: changed ? tasks : tasksIn, people, changed };
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function startOfWeekISO(iso) {
  const d = parseISO(iso);
  const day = d.getDay();
  return addDays(iso, -day);
}
function startOfMonthISO(iso) {
  const d = parseISO(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}
function addMonthsISO(iso, count) {
  const d = parseISO(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth() + count, 1));
}
function buildMonthMatrix(iso) {
  const monthStart = startOfMonthISO(iso);
  const gridStart = startOfWeekISO(monthStart);
  const targetMonth = parseISO(monthStart).getMonth();
  const weeks = [];
  let cursor = gridStart;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push({ date: cursor, inMonth: parseISO(cursor).getMonth() === targetMonth });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
    if (parseISO(cursor).getMonth() !== targetMonth && w >= 3) break;
  }
  return weeks;
}
function formatDayHeader(iso) {
  return parseISO(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function formatWeekRange(iso) {
  const start = startOfWeekISO(iso);
  const end = addDays(start, 6);
  const s = parseISO(start), e = parseISO(end);
  const sameMonth = s.getMonth() === e.getMonth();
  const sLabel = s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const eLabel = e.toLocaleDateString(undefined, sameMonth ? { day: "numeric", year: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
  return `${sLabel} – ${eLabel}`;
}
function formatMonthLabel(iso) {
  const d = parseISO(iso);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

const emptyTaskDraft = () => ({
  id: null, title: "", description: "", category: "ballot", status: "not_started",
  assignees: [], docRef: "", milestoneUnit: "days", milestoneAmount: 30, milestoneSide: "before", history: [],
});
const emptyElectionDraft = () => ({ id: null, name: "", date: "" });

export default function App({ session, onSignedOut }) {
  const [elections, setElections] = useState([]);
  const [activeElectionId, setActiveElectionId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [meta, setMeta] = useState({ lastBackupAt: null, lastBackupBy: null });
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCompleted, setShowCompleted] = useState(true);
  const [onlyMine, setOnlyMine] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [draft, setDraft] = useState(emptyTaskDraft());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [historyViewTask, setHistoryViewTask] = useState(null);
  const [electionPickerOpen, setElectionPickerOpen] = useState(false);
  const [electionModalOpen, setElectionModalOpen] = useState(false);
  const [electionDraft, setElectionDraft] = useState(emptyElectionDraft());
  const [confirmDeleteElection, setConfirmDeleteElection] = useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [starterOpen, setStarterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'calendar' | 'gantt'
  const [calendarSubView, setCalendarSubView] = useState("month"); // 'day' | 'week' | 'month'
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [adminOpen, setAdminOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lastFailedSave, setLastFailedSave] = useState(null);

  const syncerRef = useRef(null);
  if (!syncerRef.current) syncerRef.current = new Syncer();
  const syncer = syncerRef.current;
  const canAdmin = !!session.canAdminister;
  // An ended 30-day demo keeps its data readable (and downloadable as a backup) but saves nothing.
  // The shared Anytown demo office is read-only too: anyone can look, nobody can save.
  const readOnly = session.access === "expired" || session.access === "readonly";
  const activeKey = `ett-active-election:${session.org.id}`;
  // 2.1.3 (suite task 67, gap 5): restoring a backup and deleting an election need a confirmed
  // sign-in; the server decides and refuses otherwise. A Poll Worker Management sign-in counts for
  // 15 minutes after it is made (confirmedUntil), so the page checks the clock too.
  const signinConfirmed = !!session.confirmed && (!session.confirmedUntil || Date.parse(session.confirmedUntil) > Date.now());
  const confirmInfo = { confirmed: signinConfirmed, until: session.confirmedUntil || null, help: session.confirmHelp || null };
  const needsConfirmMessage = confirmInfo.help ? confirmInfo.help.message : "Deleting an election needs a confirmed sign-in. Sign out and sign in again, then try again.";

  // The person signed in to the suite. historyEntry() stamps their name on every change.
  const currentUser = { name: session.person.name || session.person.email, email: session.person.email };

  const applyLoaded = useCallback((data) => {
    const parsedTasks = data.tasks.map(normalizeTask);
    // Older records (single-assignee tasks from the artifact era) are reshaped here; the reshaped
    // form becomes the save baseline so loading alone never rewrites every task on the server.
    syncer.synced.tasks = parsedTasks;
    const { tasks: reconciledTasks, people: reconciledPeople, changed } = reconcilePeopleAndTasks(parsedTasks, data.people);
    setElections(data.elections);
    setTasks(reconciledTasks);
    setPeople(reconciledPeople);
    setMeta(data.meta || {});
    return { elections: data.elections, tasks: reconciledTasks, people: reconciledPeople, changed };
  }, [syncer]);

  const reload = useCallback(async () => applyLoaded(await syncer.load()), [applyLoaded, syncer]);

  useEffect(() => {
    (async () => {
      try {
        const r = await reload();
        let stored = null;
        try { stored = localStorage.getItem(activeKey); } catch (e) {}
        const today = todayISO();
        const upcoming = [...r.elections].sort((a, b) => dayDiff(a.date, b.date));
        const defaultActive =
          (stored && r.elections.find((e) => e.id === stored)) ||
          upcoming.find((e) => dayDiff(e.date, today) >= 0) ||
          upcoming[upcoming.length - 1];
        setActiveElectionId(defaultActive ? defaultActive.id : null);
        // Directory entries created for assignees who didn't have one yet.
        if (r.changed && !readOnly) syncer.save({ tasks: r.tasks, people: r.people }).catch(() => {});
      } catch (e) {
        setError(`Couldn't load your office's tasks. ${e.message || ""}`);
      }
      setLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picks up colleagues' changes when you come back to the tab, and once a minute while it's open,
  // but never while one of your own saves is still on its way.
  useEffect(() => {
    if (!loaded) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible" && syncer.idle()) reload().catch(() => {});
    };
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [loaded, reload, syncer]);

  const save = useCallback(async (updates) => {
    if (readOnly) {
      setError(session.readOnlyReason === "viewer"
        ? "Your Poll Worker Management account is View & Print, so you can look and download here but not change anything."
        : session.access === "readonly"
        ? "This is the shared demo office, so changes aren't saved here."
        : "Your 30-day demo has ended, so changes can't be saved. You can still download a backup from the banner above.");
      return false;
    }
    setSaveState("saving");
    try {
      await syncer.save(updates);
      setSaveState("saved");
      setError("");
      setLastFailedSave(null);
      setTimeout(() => setSaveState("idle"), 1200);
      return true;
    } catch (e) {
      setSaveState("idle");
      if (e instanceof ApiError && e.status === 409) {
        await reload().catch(() => {});
        setLastFailedSave(null);
        setError("Someone else changed this at the same time, so your last change wasn't saved. The latest version is showing now; please make your change again.");
      } else if (e instanceof ApiError && (e.status === 402 || e.status === 403)) {
        setLastFailedSave(null);
        setError(e.message);
        // 2.1.3: an election delete refused for want of a confirmed sign-in. The page had already
        // taken it off the screen, so put it back, and pick up the sign-in's current state.
        if (e.body && e.body.needsConfirm) {
          await reload().catch(() => {});
          onSignedOut();
        }
      } else {
        console.error("Save failed:", e);
        setError(`Couldn't save. Your changes may not persist.${e?.message ? ` (${e.message})` : ""}`);
        setLastFailedSave(updates);
      }
      return false;
    }
  }, [readOnly, reload, syncer, onSignedOut]);

  const persistTasks = useCallback((next) => save({ tasks: next }), [save]);
  const persistElections = useCallback((next) => save({ elections: next }), [save]);
  const persistPeople = useCallback((next) => save({ people: next }), [save]);

  function retryLastSave() {
    if (lastFailedSave) save(lastFailedSave);
  }

  useEffect(() => {
    if (!loaded || !activeElectionId) return;
    try { localStorage.setItem(activeKey, activeElectionId); } catch (e) {}
  }, [activeElectionId, loaded, activeKey]);

  async function signOut() {
    if (session.devSession) {
      await devLogout().catch(() => {});
      onSignedOut();
    } else if (session.signOutUrl) {
      window.location.href = session.signOutUrl;
    }
  }

  // Built from the server's current copy, not this tab's, so a backup always matches what is
  // actually saved, including colleagues' latest changes.
  async function downloadBackup() {
    try {
      const data = await fetchData();
      const takenAt = new Date();
      const blob = await buildBackupZip({ elections: data.elections, tasks: data.tasks, people: data.people, org: session.org, by: currentUser.name, takenAt });
      downloadBlob(blob, backupFilename(session.org.id, takenAt));
      if (session.access === "readonly") return;
      try {
        const r = await markBackupTaken();
        setMeta((m) => ({ ...m, lastBackupAt: r.lastBackupAt, lastBackupBy: r.lastBackupBy }));
        setNotice("Backup downloaded. Keep this somewhere safe — anyone with the file can restore it into this system.");
      } catch (e) {
        setNotice("Backup downloaded, but the shared \"Last backup\" indicator could not be saved. Check your connection and take the backup again so your colleagues can see it.");
      }
    } catch (e) {
      console.error("backup failed", e);
      setError(`Backup failed. Please try again.${e?.message ? ` (${e.message})` : ""}`);
    }
  }

  async function restoreFromBackup(collections, officeCheck) {
    try {
      await restoreBackup(collections, officeCheck);
    } catch (e) {
      // 2.1.3: refused for want of a confirmed sign-in: pick up the sign-in's current state so the
      // Backup & Restore card says what to do.
      if (e instanceof ApiError && e.body && e.body.needsConfirm) onSignedOut();
      throw e;
    }
    const r = await reload();
    const stillThere = r.elections.find((e) => e.id === activeElectionId);
    if (!stillThere) {
      const upcoming = [...r.elections].sort((a, b) => dayDiff(a.date, b.date));
      const next = upcoming.find((e) => dayDiff(e.date, todayISO()) >= 0) || upcoming[upcoming.length - 1];
      setActiveElectionId(next ? next.id : null);
    }
    setNotice(`Restore complete: ${r.elections.length} election${r.elections.length === 1 ? "" : "s"}, ${r.tasks.length} task${r.tasks.length === 1 ? "" : "s"} and ${r.people.length} ${r.people.length === 1 ? "person" : "people"} are now in the tracker.`);
  }
  const activeElection = elections.find((e) => e.id === activeElectionId) || null;
  const today = todayISO();
  const daysToElection = activeElection ? dayDiff(activeElection.date, today) : 0;

  const sortedElections = useMemo(
    () => [...elections].sort((a, b) => dayDiff(a.date, b.date)),
    [elections]
  );

  const electionTasks = useMemo(
    () => tasks.filter((t) => t.electionId === activeElectionId),
    [tasks, activeElectionId]
  );

  const tasksWithDates = useMemo(
    () =>
      activeElection
        ? electionTasks.map((t) => ({
            ...t,
            dueDate: milestoneToDate(activeElection.date, { unit: t.milestoneUnit, amount: t.milestoneAmount, side: t.milestoneSide }),
            label: milestoneLabel({ unit: t.milestoneUnit, amount: t.milestoneAmount, side: t.milestoneSide }),
          }))
        : [],
    [electionTasks, activeElection]
  );

  // "My tasks" matches on email first (what the suite account knows for sure), then on name for
  // assignees added without an email.
  const myEmail = (currentUser.email || "").trim().toLowerCase();
  const myName = (currentUser.name || "").trim().toLowerCase();
  function isMine(t) {
    return (t.assignees || []).some((a) => {
      const e = (a.email || "").trim().toLowerCase();
      if (myEmail && e) return e === myEmail;
      return !!myName && (a.name || "").trim().toLowerCase() === myName;
    });
  }

  const filtered = useMemo(() => {
    return tasksWithDates.filter((t) => {
      if (!showCompleted && t.status === "completed") return false;
      if (onlyMine && !isMine(t)) return false;
      if (filterCategory !== "all" && t.category !== filterCategory) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !t.title.toLowerCase().includes(q) &&
          !t.description.toLowerCase().includes(q) &&
          !(t.assignees || []).some((a) => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
        ) return false;
      }
      return true;
    });
  }, [tasksWithDates, showCompleted, onlyMine, filterCategory, filterStatus, search]);

  const groups = useMemo(() => {
    if (!activeElection) return [];
    const buckets = {
      overdue: { label: "Overdue", items: [] },
      week: { label: "Next 7 Days", items: [] },
      month: { label: "Next 30 Days", items: [] },
      quarter: { label: "31–90 Days Out", items: [] },
      far: { label: "90+ Days Out", items: [] },
      post: { label: "Post-Election", items: [] },
    };
    filtered.forEach((t) => {
      const diff = dayDiff(t.dueDate, today);
      if (t.status !== "completed" && diff < 0 && dayDiff(t.dueDate, activeElection.date) <= 0) {
        buckets.overdue.items.push(t);
      } else if (dayDiff(t.dueDate, activeElection.date) > 0) {
        buckets.post.items.push(t);
      } else if (diff <= 7) {
        buckets.week.items.push(t);
      } else if (diff <= 30) {
        buckets.month.items.push(t);
      } else if (diff <= 90) {
        buckets.quarter.items.push(t);
      } else {
        buckets.far.items.push(t);
      }
    });
    Object.values(buckets).forEach((b) => b.items.sort((a, b2) => dayDiff(a.dueDate, b2.dueDate)));
    return Object.values(buckets).filter((b) => b.items.length > 0);
  }, [filtered, today, activeElection]);

  const stats = useMemo(() => {
    if (!activeElection) return { overdue: 0, dueSoon: 0, inProgress: 0, completed: 0, total: 0 };
    const overdue = tasksWithDates.filter((t) => t.status !== "completed" && dayDiff(t.dueDate, today) < 0 && dayDiff(t.dueDate, activeElection.date) <= 0).length;
    const dueSoon = tasksWithDates.filter((t) => t.status !== "completed" && dayDiff(t.dueDate, today) >= 0 && dayDiff(t.dueDate, today) <= 7).length;
    const inProgress = tasksWithDates.filter((t) => t.status === "in_progress").length;
    const completed = tasksWithDates.filter((t) => t.status === "completed").length;
    return { overdue, dueSoon, inProgress, completed, total: tasksWithDates.length };
  }, [tasksWithDates, today, activeElection]);

  function openAddTask() { setDraft(emptyTaskDraft()); setTaskModalOpen(true); }
  function openEditTask(task) {
    setDraft({
      id: task.id, title: task.title, description: task.description, category: task.category, status: task.status,
      assignees: (task.assignees || []).map((a) => ({ ...a })), docRef: task.docRef, milestoneUnit: task.milestoneUnit, milestoneAmount: task.milestoneAmount, milestoneSide: task.milestoneSide,
      history: task.history || [],
    });
    setTaskModalOpen(true);
  }
  function historyEntry(text) {
    return { id: uid(), at: new Date().toISOString(), by: currentUser.name || "Unknown", text };
  }
  function duplicateTask(task) {
    const copy = {
      id: uid(),
      electionId: task.electionId,
      title: `COPY OF ${task.title}`,
      description: task.description,
      category: task.category,
      status: task.status,
      assignees: (task.assignees || []).map((a) => ({ ...a, id: uid() })),
      docRef: task.docRef,
      milestoneUnit: task.milestoneUnit,
      milestoneAmount: task.milestoneAmount,
      milestoneSide: task.milestoneSide,
      history: [historyEntry(`Copied from "${task.title}"`)],
    };
    const next = [...tasks, copy];
    setTasks(next);
    persistTasks(next);
    openEditTask(copy);
  }
  function saveDraft() {
    if (!draft.title.trim() || !activeElectionId) return;
    let next;
    if (draft.id) {
      next = tasks.map((t) => {
        if (t.id !== draft.id) return t;
        const history = [...(t.history || [])];
        if (t.status !== draft.status) {
          history.push(historyEntry(`Status changed to "${STATUSES.find((s) => s.id === draft.status)?.label || draft.status}"`));
        }
        const assigneeIds = (arr) => JSON.stringify((arr || []).map((a) => a.personId || a.name + a.email).sort());
        const otherChanged =
          t.title !== draft.title || t.description !== draft.description || t.category !== draft.category ||
          t.docRef !== draft.docRef || t.milestoneUnit !== draft.milestoneUnit || Number(t.milestoneAmount) !== Number(draft.milestoneAmount) ||
          t.milestoneSide !== draft.milestoneSide || assigneeIds(t.assignees) !== assigneeIds(draft.assignees);
        if (otherChanged) history.push(historyEntry("Task details updated"));
        return { ...t, ...draft, history };
      });
    } else {
      next = [...tasks, { ...draft, id: uid(), electionId: activeElectionId, history: [historyEntry("Task created")] }];
    }
    setTasks(next);
    persistTasks(next);
    setTaskModalOpen(false);
  }
  function deleteTask(id) {
    const next = tasks.filter((t) => t.id !== id);
    setTasks(next);
    persistTasks(next);
    setConfirmDelete(null);
  }
  function quickSetStatus(task, status) {
    const next = tasks.map((t) =>
      t.id === task.id
        ? { ...t, status, history: [...(t.history || []), historyEntry(`Status changed to "${STATUSES.find((s) => s.id === status)?.label || status}"`)] }
        : t
    );
    setTasks(next);
    persistTasks(next);
  }

  function openAddElection() { setElectionDraft(emptyElectionDraft()); setElectionModalOpen(true); }
  function openEditElection(el) { setElectionDraft({ id: el.id, name: el.name, date: el.date }); setElectionModalOpen(true); }
  function saveElectionDraft() {
    if (!electionDraft.name.trim() || !electionDraft.date) return;
    let next;
    if (electionDraft.id) {
      next = elections.map((e) => (e.id === electionDraft.id ? { ...electionDraft } : e));
    } else {
      const newEl = { ...electionDraft, id: uid() };
      next = [...elections, newEl];
      setActiveElectionId(newEl.id);
    }
    setElections(next);
    persistElections(next);
    setElectionModalOpen(false);
  }
  function deleteElection(el) {
    // 2.1.3: the server refuses this without a confirmed sign-in; say so before anything changes.
    if (!signinConfirmed) {
      setConfirmDeleteElection(null);
      setError(needsConfirmMessage);
      return;
    }
    const nextElections = elections.filter((e) => e.id !== el.id);
    const nextTasks = tasks.filter((t) => t.electionId !== el.id);
    setElections(nextElections);
    setTasks(nextTasks);
    // One save, so an election is never deleted while its tasks survive (or the reverse).
    save({ elections: nextElections, tasks: nextTasks });
    if (activeElectionId === el.id) {
      setActiveElectionId(nextElections[0] ? nextElections[0].id : null);
    }
    setConfirmDeleteElection(null);
  }

  function copyTasksFrom(sourceElectionId, resetStatus) {
    if (!activeElectionId || sourceElectionId === activeElectionId) return;
    const sourceTasks = tasks.filter((t) => t.electionId === sourceElectionId);
    if (sourceTasks.length === 0) return;
    const copied = sourceTasks.map((t) => ({
      ...t,
      id: uid(),
      electionId: activeElectionId,
      status: resetStatus ? "not_started" : t.status,
    }));
    const next = [...tasks, ...copied];
    setTasks(next);
    persistTasks(next);
    setCopyModalOpen(false);
  }

  // Finds an existing directory entry by name+email (case-insensitive), or creates one.
  // Used whenever someone types a new assignee name instead of picking from the list.
  function addOrGetPerson(name, email) {
    const key = (n, e) => `${(n || "").trim().toLowerCase()}|${(e || "").trim().toLowerCase()}`;
    const existing = people.find((p) => key(p.name, p.email) === key(name, email));
    if (existing) return existing;
    const person = { id: uid(), name: (name || "").trim(), email: (email || "").trim() };
    const next = [...people, person];
    setPeople(next);
    persistPeople(next);
    return person;
  }

  function addPersonManually(name, email) {
    if (!name.trim() && !email.trim()) return;
    addOrGetPerson(name, email);
  }

  function countTasksForPerson(personId) {
    return tasks.reduce((sum, t) => sum + ((t.assignees || []).some((a) => a.personId === personId) ? 1 : 0), 0);
  }
  function tasksForPerson(personId) {
    return tasks
      .filter((t) => (t.assignees || []).some((a) => a.personId === personId))
      .map((t) => ({ task: t, election: elections.find((e) => e.id === t.electionId) }));
  }
  const orphanedTasks = useMemo(
    () => tasks.filter((t) => !elections.some((e) => e.id === t.electionId)),
    [tasks, elections]
  );
  const auditEntries = useMemo(() => {
    const entries = [];
    tasks.forEach((t) => {
      (t.history || []).forEach((h) => {
        entries.push({ ...h, task: t, election: elections.find((e) => e.id === t.electionId) || null });
      });
    });
    return entries.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [tasks, elections]);

  // Jumps from the Admin audit trail to the actual task: switches to its election
  // (if it still exists) and opens it in the edit form so someone can review or fix it.
  function reviewTaskFromAudit(task) {
    if (task.electionId && elections.some((e) => e.id === task.electionId)) {
      setActiveElectionId(task.electionId);
    }
    setAdminOpen(false);
    openEditTask(task);
  }

  // Removes a person from the directory. Any tasks assigned to them either get reassigned
  // to another directory person, or simply lose that assignee if left unassigned.
  async function removePerson(personId, reassignToPersonId) {
    const reassignTo = reassignToPersonId ? people.find((p) => p.id === reassignToPersonId) : null;
    const nextTasks = tasks.map((t) => {
      const hasPerson = (t.assignees || []).some((a) => a.personId === personId);
      if (!hasPerson) return t;
      let assignees;
      if (reassignTo) {
        const alreadyHasReassignee = t.assignees.some((a) => a.personId === reassignTo.id);
        assignees = t.assignees
          .map((a) => (a.personId === personId ? (alreadyHasReassignee ? null : { ...a, personId: reassignTo.id, name: reassignTo.name, email: reassignTo.email }) : a))
          .filter(Boolean);
      } else {
        assignees = t.assignees.filter((a) => a.personId !== personId);
      }
      return { ...t, assignees };
    });
    const nextPeople = people.filter((p) => p.id !== personId);
    setTasks(nextTasks);
    setPeople(nextPeople);
    // One all-or-nothing save, so tasks never point at someone who's no longer on the list.
    await save({ tasks: nextTasks, people: nextPeople });
  }

  // Every table downloads as Excel or CSV (2.0.2, suite task 20). A failure says what to do
  // instead of leaving the button silently doing nothing.
  async function exportTable(format, name, sheetName, rows) {
    try {
      await downloadTable(format, name, sheetName, rows);
    } catch {
      setError("The download didn't work. Try again, or try the other format (Excel or CSV).");
    }
  }
  function exportTasks(format) {
    const sorted = [...filtered].sort((a, b) => dayDiff(a.dueDate, b.dueDate));
    exportTable(format, `${fileSlug(activeElection.name)}-${activeElection.date}-tasks`, "Tasks", taskRows(sorted, (id) => catInfo(id).label));
  }
  function printList() {
    const sorted = [...filtered].sort((a, b) => dayDiff(a.dueDate, b.dueDate));
    const ok = printTasks({ tasks: sorted, election: activeElection, orgName: session.org.name, categoryLabel: (id) => catInfo(id).label, formatDate: formatNiceDate });
    if (!ok) setError("Your browser blocked the print window. Allow pop-ups for this site and try again.");
  }
  function addStarterTasks(picked) {
    if (!activeElectionId || picked.length === 0) return;
    const created = picked.map((t) => ({
      ...emptyTaskDraft(),
      ...t,
      id: uid(),
      electionId: activeElectionId,
      history: [historyEntry("Added from the starter list")],
    }));
    const next = [...tasks, ...created];
    setTasks(next);
    persistTasks(next);
    setStarterOpen(false);
  }

  return (
    <div style={styles.page}>
      <style>{globalCss}</style>
      <header style={styles.header}>
        <div style={styles.headerInner} className="ett-header-inner">
          <div style={styles.brandRow}>
            <DomeMark size={30} color={GOLD} />
            <div>
              <div style={styles.brandTitle}>{session.org.name}</div>
              <div style={styles.brandSub}>Election Task Tracker</div>
            </div>
          </div>
          <div style={styles.headerRight} className="ett-header-right">
            <div style={styles.saveIndicator}>
              {saveState === "saving" && <span style={{ color: MUTED }}>Saving…</span>}
              {saveState === "saved" && <span style={{ color: NAVY }}>Saved</span>}
            </div>
            <span style={styles.userChip} title={`Signed in as ${currentUser.email || currentUser.name} (${session.role})`}>
              <UserCircle2 size={14} /> {currentUser.name}
            </span>
            {canAdmin && (
              <button style={styles.adminBtn} onClick={() => setAdminOpen(true)}>
                <ShieldCheck size={14} /> Admin
              </button>
            )}
            {(session.devSession || session.signOutUrl) && (
              <button style={styles.adminBtn} onClick={signOut} title="Sign out">
                <LogOut size={14} /> Sign out
              </button>
            )}
          </div>
          {/* 2.0.3: the suite's 9-dot app switcher (app/SuiteSwitcher.jsx): this office's other suite
              tools, each opening in a new tab. Rightmost on a computer; on a phone it moves up beside
              the office name (CSS order below), because on its own line under the buttons it read as
              part of the page rather than the header (checked at 375px). */}
          <div className="ett-header-apps" style={styles.headerApps}>
            <SuiteSwitcher suite={session.suiteOrigin} office={session.org.id} color="#fff" accent={NAVY}
              own={session.via === "pwm" ? { officeName: session.org.name, pwmUrl: session.pwmUrl } : null} />
          </div>
        </div>
      </header>
      <div style={styles.stripe} aria-hidden="true" />

      <main style={styles.main}>
        {session.demo && (
          <DemoBanner demo={session.demo} contactUrl={session.contactUrl} onBackup={downloadBackup} />
        )}
        {canAdmin && loaded && !adminOpen && session.access !== "readonly" && (
          <BackupBanner lastBackupAt={meta.lastBackupAt} lastBackupBy={meta.lastBackupBy} onBackUpNow={() => { setAdminOpen(true); setTimeout(() => document.getElementById("backup-restore")?.scrollIntoView({ behavior: "smooth" }), 50); }} />
        )}
        {session.readOnlyReason === "viewer" ? (
          <div style={styles.sampleBanner}>
            <Clock size={15} /> Your Poll Worker Management account is View &amp; Print: you can look, print and download here, but not change anything.
          </div>
        ) : session.access === "readonly" ? (
          <div style={styles.sampleBanner}>
            <Clock size={15} /> This is the shared {session.org.name} demo office. Look around as much as you like; changes aren't saved here.
          </div>
        ) : session.sample && (
          <div style={styles.sampleBanner}>
            <Clock size={15} /> This is a practice office. Anything here is sample data.
          </div>
        )}
        {notice && (
          <div style={styles.noticeBanner} role="status">
            <CheckCircle2 size={16} /> <span style={{ flex: 1 }}>{notice}</span>
            <button style={styles.iconBtn} onClick={() => setNotice("")} aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}
        {error && (
          <div style={styles.errorBanner} role="alert">
            <AlertTriangle size={16} /> <span style={{ flex: 1 }}>{error}</span>
            {lastFailedSave && (
              <button style={styles.retryBtn} onClick={retryLastSave}>Retry save</button>
            )}
            <button style={styles.iconBtn} onClick={() => setError("")} aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}
        {adminOpen ? (
          <AdminScreen
            people={people}
            tasks={tasks}
            elections={elections}
            countTasksForPerson={countTasksForPerson}
            tasksForPerson={tasksForPerson}
            orphanedTasks={orphanedTasks}
            auditEntries={auditEntries}
            onReviewTask={reviewTaskFromAudit}
            onAddPerson={addPersonManually}
            onRemovePerson={removePerson}
            onDeleteTask={(t) => setConfirmDelete(t)}
            onDeleteElection={deleteElection}
            onBack={() => setAdminOpen(false)}
            onExportTable={exportTable}
            confirm={confirmInfo}
            backup={{ meta, onDownload: downloadBackup, onRestore: restoreFromBackup, orgId: session.org.id, orgName: session.org.name, readOnly, confirm: confirmInfo }}
          />
        ) : (
        <>
        {!loaded ? (
          <div style={{ padding: 40, textAlign: "center", color: MUTED }}>Loading…</div>
        ) : !activeElection ? (
          <div style={styles.emptyState}>
            <Vote size={28} color={GOLD} />
            <div style={styles.emptyTitle}>No elections set up yet</div>
            {canAdmin ? (
              <>
                <div style={styles.emptyBody}>Add an election to start tracking tasks against its countdown.</div>
                {!readOnly && <button style={styles.primaryBtn} onClick={openAddElection}><Plus size={16} /> Add Election</button>}
              </>
            ) : (
              <div style={styles.emptyBody}>An owner or admin in your office needs to add the first election.</div>
            )}
          </div>
        ) : (
          <>
            <div style={styles.selectorRow}>
              <ElectionSelector
                elections={sortedElections}
                activeElection={activeElection}
                open={electionPickerOpen}
                setOpen={setElectionPickerOpen}
                onSelect={(id) => { setActiveElectionId(id); setElectionPickerOpen(false); }}
                onAdd={openAddElection}
                onEdit={openEditElection}
                onDelete={(el) => {
                  // 2.1.3: say so straight away rather than after the confirmation dialog.
                  if (signinConfirmed) return setConfirmDeleteElection(el);
                  setElectionPickerOpen(false);
                  setError(needsConfirmMessage);
                }}
                canManage={canAdmin && !readOnly}
                today={today}
              />
              {elections.length > 1 && !readOnly && (
                <button style={styles.copyTasksBtn} onClick={() => setCopyModalOpen(true)}>
                  <Copy size={14} /> Copy Tasks From…
                </button>
              )}
            </div>

            <Hero activeElection={activeElection} daysToElection={daysToElection} />

            <StatsRow stats={stats} />

            <div style={styles.viewToggleRow}>
              <div style={styles.viewToggleGroup}>
                <button style={{ ...styles.viewToggleBtn, ...(viewMode === "list" ? styles.viewToggleBtnActive : {}) }} onClick={() => setViewMode("list")}>
                  <List size={14} /> List
                </button>
                <button style={{ ...styles.viewToggleBtn, ...(viewMode === "calendar" ? styles.viewToggleBtnActive : {}) }} onClick={() => setViewMode("calendar")}>
                  <CalendarDays size={14} /> Calendar
                </button>
                <button style={{ ...styles.viewToggleBtn, ...(viewMode === "gantt" ? styles.viewToggleBtnActive : {}) }} onClick={() => setViewMode("gantt")}>
                  <BarChart3 size={14} /> Gantt
                </button>
              </div>
              {viewMode === "calendar" && (
                <div style={styles.viewToggleGroup}>
                  {["day", "week", "month"].map((sv) => (
                    <button key={sv} style={{ ...styles.viewToggleBtn, ...(calendarSubView === sv ? styles.viewToggleBtnActive : {}) }} onClick={() => setCalendarSubView(sv)}>
                      {sv.charAt(0).toUpperCase() + sv.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Toolbar
              search={search} setSearch={setSearch}
              filterCategory={filterCategory} setFilterCategory={setFilterCategory}
              filterStatus={filterStatus} setFilterStatus={setFilterStatus}
              showCompleted={showCompleted} setShowCompleted={setShowCompleted}
              onlyMine={onlyMine} setOnlyMine={setOnlyMine}
              onAdd={readOnly ? null : openAddTask}
              onExport={exportTasks}
              onPrint={printList}
              canExport={filtered.length > 0}
            />

            {filtered.length === 0 && electionTasks.length > 0 && (
              <div style={styles.emptyState}>
                <Search size={24} color={MUTED} />
                <div style={styles.emptyTitle}>No tasks match these filters</div>
                <div style={styles.emptyBody}>{onlyMine ? "Nothing in this election is assigned to you. " : ""}Try clearing the search or filters.</div>
              </div>
            )}

            {electionTasks.length === 0 && (
              <div style={styles.emptyState}>
                <Landmark size={28} color={GOLD} />
                <div style={styles.emptyTitle}>No tasks yet for {activeElection.name}</div>
                <div style={styles.emptyBody}>
                  Add the first task on the road to Election Day: set how far before or after Election Day it's due, and it lands in the right timeline group automatically. Or start from a ready-made list of common tasks and adjust it.
                </div>
                {!readOnly && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                    <button style={styles.primaryBtn} onClick={() => setStarterOpen(true)}><ListChecks size={16} /> Start from the starter list</button>
                    <button style={styles.secondaryBtn} onClick={openAddTask}><Plus size={16} /> Add a task</button>
                  </div>
                )}
              </div>
            )}

            {filtered.length > 0 && viewMode === "list" && groups.map((g) => (
              <section key={g.label} style={styles.group}>
                <div style={styles.groupHeader}>
                  <span>{g.label}</span>
                  <span style={styles.groupCount}>{g.items.length}</span>
                </div>
                <div style={styles.taskList}>
                  {g.items.map((t) => (
                    <TaskRow key={t.id} task={t} today={today} onEdit={() => openEditTask(t)} onDuplicate={() => duplicateTask(t)} onDelete={() => setConfirmDelete(t)} onStatus={(s) => quickSetStatus(t, s)} onViewHistory={setHistoryViewTask} />
                  ))}
                </div>
              </section>
            ))}

            {filtered.length > 0 && viewMode === "calendar" && (
              <CalendarView
                tasks={filtered}
                activeElection={activeElection}
                today={today}
                subView={calendarSubView}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                onDayClick={(d) => { setSelectedDate(d); setCalendarSubView("day"); }}
                onEditTask={openEditTask}
                onDuplicateTask={duplicateTask}
                onDeleteTask={(t) => setConfirmDelete(t)}
                onStatus={quickSetStatus}
                onViewHistory={setHistoryViewTask}
              />
            )}

            {filtered.length > 0 && viewMode === "gantt" && (
              <GanttView tasks={filtered} activeElection={activeElection} today={today} onEditTask={openEditTask} />
            )}
          </>
        )}
        </>
        )}
      </main>

      {taskModalOpen && activeElection && (
        <TaskModal draft={draft} setDraft={setDraft} electionDate={activeElection.date} people={people} onEnsurePerson={addOrGetPerson} onCancel={() => setTaskModalOpen(false)} onSave={saveDraft} />
      )}
      {confirmDelete && <ConfirmModal title="Delete this task?" body={`"${confirmDelete.title}" will be removed for everyone using this tracker. This can't be undone.`} onCancel={() => setConfirmDelete(null)} onConfirm={() => deleteTask(confirmDelete.id)} />}
      {historyViewTask && <TaskHistoryModal task={historyViewTask} onClose={() => setHistoryViewTask(null)} />}

      {electionModalOpen && <ElectionModal draft={electionDraft} setDraft={setElectionDraft} onCancel={() => setElectionModalOpen(false)} onSave={saveElectionDraft} />}
      {copyModalOpen && activeElection && (
        <CopyTasksModal
          elections={sortedElections.filter((e) => e.id !== activeElectionId)}
          tasks={tasks}
          activeElectionName={activeElection.name}
          onCancel={() => setCopyModalOpen(false)}
          onCopy={copyTasksFrom}
        />
      )}
      {confirmDeleteElection && (
        <ConfirmModal
          title="Delete this election?"
          body={`"${confirmDeleteElection.name}" (${formatNiceDate(confirmDeleteElection.date)}) and all of its tasks will be removed for everyone. This can't be undone.`}
          onCancel={() => setConfirmDeleteElection(null)}
          onConfirm={() => deleteElection(confirmDeleteElection)}
        />
      )}
      {starterOpen && activeElection && (
        <StarterTasksModal electionName={activeElection.name} electionDate={activeElection.date} onCancel={() => setStarterOpen(false)} onAdd={addStarterTasks} />
      )}
    </div>
  );
}

function DomeMark({ size = 24, color = GOLD }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="4" r="2" fill={color} />
      <rect x="19" y="6" width="2" height="4" fill={color} />
      <path d="M10 16C10 10 14 6 20 6C26 6 30 10 30 16" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M12 16L12 26" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M20 16L20 26" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M28 16L28 26" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M6 30C10 26 30 26 34 30" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M4 34C10 30 30 30 36 34" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function ElectionSelector({ elections, activeElection, open, setOpen, onSelect, onAdd, onEdit, onDelete, today, canManage }) {
  return (
    <div style={styles.electionSelectorWrap}>
      <button style={styles.electionPickerBtn} onClick={() => setOpen(!open)}>
        <Vote size={15} color={GOLD} />
        <span style={styles.electionPickerName}>{activeElection.name}</span>
        <span style={styles.electionPickerDate}>{formatNiceDate(activeElection.date)}</span>
        <ChevronDown size={15} color={MUTED} />
      </button>
      {open && (
        <div style={styles.electionDropdown}>
          {elections.map((el) => {
            const past = dayDiff(el.date, today) < 0;
            return (
              <div key={el.id} style={{ ...styles.electionOption, background: el.id === activeElection.id ? PAPER : "transparent" }}>
                <button style={styles.electionOptionMain} onClick={() => onSelect(el.id)}>
                  <div>
                    <div style={styles.electionOptionName}>{el.name}</div>
                    <div style={styles.electionOptionDate}>{formatNiceDate(el.date)}{past ? " · Past" : ""}</div>
                  </div>
                </button>
                {canManage && <button style={styles.iconBtn} onClick={() => onEdit(el)} aria-label="Edit election"><Pencil size={13} /></button>}
                {canManage && <button style={styles.iconBtn} onClick={() => onDelete(el)} aria-label="Delete election"><Trash2 size={13} /></button>}
              </div>
            );
          })}
          {canManage && <button style={styles.electionAddOption} onClick={onAdd}><Plus size={14} /> Add Election</button>}
        </div>
      )}
    </div>
  );
}

function Hero({ activeElection, daysToElection }) {
  const past = daysToElection < 0;
  return (
    <div style={styles.hero} className="ett-hero">
      <div style={styles.heroLeft}>
        <div style={styles.seal}>
          <DomeMark size={26} color={GOLD} />
          <div style={styles.sealNumber}>{Math.abs(daysToElection)}</div>
          <div style={styles.sealLabel}>{past ? "DAYS SINCE" : "DAYS TO"}</div>
        </div>
      </div>
      <div style={styles.heroRight}>
        <div style={styles.heroEyebrow}>{activeElection.name}</div>
        <div style={styles.heroDate}>{formatNiceDate(activeElection.date)}</div>
        <div style={styles.heroHint}>Switch elections or edit this date using the selector above.</div>
      </div>
    </div>
  );
}

function StatsRow({ stats }) {
  const items = [
    { label: "Overdue", value: stats.overdue, color: RED },
    { label: "Due in 7 Days", value: stats.dueSoon, color: GOLD },
    { label: "In Progress", value: stats.inProgress, color: NAVY },
    { label: "Completed", value: stats.completed, color: MUTED },
    { label: "Total Tasks", value: stats.total, color: INK },
  ];
  return (
    <div style={styles.statsRow} className="ett-stats">
      {items.map((it) => (
        <div key={it.label} style={styles.statCard}>
          <div style={{ ...styles.statValue, color: it.color }}>{it.value}</div>
          <div style={styles.statLabel}>{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function Toolbar({ search, setSearch, filterCategory, setFilterCategory, filterStatus, setFilterStatus, showCompleted, setShowCompleted, onlyMine, setOnlyMine, onAdd, onExport, onPrint, canExport }) {
  return (
    <div style={styles.toolbar}>
      <div style={styles.searchWrap}>
        <Search size={15} color={MUTED} />
        <input style={styles.searchInput} placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <select style={styles.select} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
        <option value="all">All Categories</option>
        {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
        <option value="all">All Statuses</option>
        {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <label style={styles.checkboxLabel}>
        <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />
        Show completed
      </label>
      <label style={styles.checkboxLabel}>
        <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
        My tasks
      </label>
      <ExportButtons onExport={onExport} disabled={!canExport} what="the tasks shown" />
      <button style={styles.secondaryBtn} onClick={onPrint} disabled={!canExport} title="Print the tasks shown"><Printer size={15} /> Print</button>
      {onAdd && <button style={styles.primaryBtn} onClick={onAdd}><Plus size={16} /> Add Task</button>}
    </div>
  );
}

// "Excel" and "CSV" download buttons for one table; what: the words for the tooltip.
function ExportButtons({ onExport, disabled, what }) {
  return (
    <>
      <button style={styles.secondaryBtn} onClick={() => onExport("xlsx")} disabled={disabled} title={`Download ${what} as an Excel workbook (.xlsx)`}><FileSpreadsheet size={15} /> Excel</button>
      <button style={styles.secondaryBtn} onClick={() => onExport("csv")} disabled={disabled} title={`Download ${what} as a CSV file`}><FileSpreadsheet size={15} /> CSV</button>
    </>
  );
}

function StatusPill({ status, onChange }) {
  // Gold text on white is too faint to read, so "In Progress" uses a darker gold.
  const colorMap = { not_started: MUTED, in_progress: "#7a5a00", blocked: RED, completed: "#1d6b45" };
  return (
    <select aria-label="Task status" value={status} onChange={(e) => onChange(e.target.value)} style={{ ...styles.statusPill, color: colorMap[status], borderColor: colorMap[status], background: status === "completed" ? "#e5f3ec" : "#fff" }}>
      {STATUSES.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
    </select>
  );
}

function TaskDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 160;
  const shown = expanded || !isLong ? text : text.slice(0, 155).trimEnd() + "…";
  return (
    <div style={styles.taskDesc}>
      {shown}
      {isLong && (
        <button style={styles.showMoreBtn} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function TaskRow({ task, today, onEdit, onDuplicate, onDelete, onStatus, onViewHistory }) {
  const cat = catInfo(task.category);
  const isOverdue = task.status !== "completed" && dayDiff(task.dueDate, today) < 0;
  const isLink = /^https?:\/\//i.test(task.docRef || "");
  return (
    <div className="ett-task-row" style={{ ...styles.taskRow, opacity: task.status === "completed" ? 0.65 : 1 }}>
      <div style={{ ...styles.milestoneBadge, borderColor: isOverdue ? RED : NAVY, color: isOverdue ? RED : NAVY }}>{task.label}</div>
      <div className="ett-task-main" style={styles.taskMain}>
        <div style={styles.taskTitleRow}>
          <span style={{ ...styles.taskTitle, textDecoration: task.status === "completed" ? "line-through" : "none" }}>{task.title}</span>
          <span style={{ ...styles.catTag, background: cat.color + "1a", color: cat.color }}>{cat.label}</span>
        </div>
        {task.description && <TaskDescription text={task.description} />}
        <div style={styles.taskMeta}>
          <span>{formatNiceDate(task.dueDate)}</span>
          {task.assignees && task.assignees.length > 0 && (
            <span style={styles.assigneeWrap}>
              · Assigned:{" "}
              {task.assignees.map((a, i) => (
                <span key={a.id || i} style={styles.assigneeItem}>
                  {a.name || a.email}
                  {a.email && (
                    <a href={`mailto:${a.email}`} style={styles.emailLink} title={`Email ${a.name || a.email}`}>
                      <Mail size={11} />
                    </a>
                  )}
                  {i < task.assignees.length - 1 ? "," : ""}
                </span>
              ))}
            </span>
          )}
          {task.docRef && (
            <span style={styles.docRefWrap}>
              ·{" "}
              {isLink ? (
                <a href={task.docRef} target="_blank" rel="noreferrer" style={styles.docLink}><Link2 size={12} /> Document <ExternalLink size={10} /></a>
              ) : (
                <span style={styles.docLink}><Link2 size={12} /> {task.docRef}</span>
              )}
            </span>
          )}
        </div>
      </div>

      <div style={styles.taskActions}>
        <StatusPill status={task.status} onChange={onStatus} />
        {task.history && task.history.length > 0 && (
          <button style={styles.iconBtn} onClick={() => onViewHistory(task)} aria-label="View history" title="View activity history (read-only)"><History size={15} /></button>
        )}
        <button style={styles.iconBtn} onClick={onDuplicate} aria-label="Duplicate task" title="Duplicate task"><Copy size={15} /></button>
        <button style={styles.iconBtn} onClick={onEdit} aria-label="Edit task"><Pencil size={15} /></button>
        <button style={styles.iconBtn} onClick={onDelete} aria-label="Delete task"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

function statusColor(status) {
  return { not_started: MUTED, in_progress: GOLD, blocked: RED, completed: NAVY }[status] || MUTED;
}

function CalendarNav({ label, onPrev, onNext, onToday }) {
  return (
    <div style={styles.calNavRow}>
      <div style={styles.calNavLabel}>{label}</div>
      <div style={styles.calNavBtns}>
        <button style={styles.calNavBtn} onClick={onToday}>Today</button>
        <button style={styles.calNavIconBtn} onClick={onPrev} aria-label="Previous"><ChevronLeft size={16} /></button>
        <button style={styles.calNavIconBtn} onClick={onNext} aria-label="Next"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function CalendarView({ tasks, activeElection, today, subView, selectedDate, setSelectedDate, onDayClick, onEditTask, onDuplicateTask, onDeleteTask, onStatus, onViewHistory }) {
  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!map[t.dueDate]) map[t.dueDate] = [];
      map[t.dueDate].push(t);
    });
    return map;
  }, [tasks]);

  function shift(amount) {
    if (subView === "day") setSelectedDate(addDays(selectedDate, amount));
    else if (subView === "week") setSelectedDate(addDays(selectedDate, amount * 7));
    else setSelectedDate(addMonthsISO(selectedDate, amount));
  }
  function goToday() { setSelectedDate(today); }

  const label = subView === "day" ? formatDayHeader(selectedDate) : subView === "week" ? formatWeekRange(selectedDate) : formatMonthLabel(selectedDate);

  return (
    <div>
      <CalendarNav label={label} onPrev={() => shift(-1)} onNext={() => shift(1)} onToday={goToday} />
      {subView === "month" && (
        <MonthGrid selectedDate={selectedDate} tasksByDate={tasksByDate} today={today} activeElection={activeElection} onDayClick={onDayClick} onEditTask={onEditTask} />
      )}
      {subView === "week" && (
        <WeekGrid selectedDate={selectedDate} tasksByDate={tasksByDate} today={today} activeElection={activeElection} onDayClick={onDayClick} onEditTask={onEditTask} />
      )}
      {subView === "day" && (
        <DayList selectedDate={selectedDate} tasks={tasksByDate[selectedDate] || []} today={today} activeElection={activeElection} onEditTask={onEditTask} onDuplicateTask={onDuplicateTask} onDeleteTask={onDeleteTask} onStatus={onStatus} onViewHistory={onViewHistory} />
      )}
    </div>
  );
}

function DayPill({ task, onEditTask }) {
  const cat = catInfo(task.category);
  return (
    <button
      style={{ ...styles.dayPill, borderLeft: `3px solid ${cat.color}`, opacity: task.status === "completed" ? 0.55 : 1 }}
      onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
      title={task.title}
    >
      <span style={{ ...styles.dayPillDot, background: statusColor(task.status) }} />
      <span style={{ textDecoration: task.status === "completed" ? "line-through" : "none" }}>{task.title}</span>
    </button>
  );
}

function MonthGrid({ selectedDate, tasksByDate, today, activeElection, onDayClick, onEditTask }) {
  const weeks = useMemo(() => buildMonthMatrix(selectedDate), [selectedDate]);
  return (
    <div style={styles.monthGrid}>
      <div style={styles.monthWeekdayRow}>
        {WEEKDAY_SHORT.map((wd) => <div key={wd} style={styles.monthWeekdayCell}>{wd}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={styles.monthWeekRow}>
          {week.map((cell) => {
            const dayTasks = tasksByDate[cell.date] || [];
            const isElectionDay = cell.date === activeElection.date;
            const isToday = cell.date === today;
            const visible = dayTasks.slice(0, 3);
            const extra = dayTasks.length - visible.length;
            return (
              <div
                key={cell.date}
                style={{
                  ...styles.monthDayCell,
                  opacity: cell.inMonth ? 1 : 0.4,
                  background: isElectionDay ? "#fbf4de" : CARD,
                  cursor: "pointer",
                }}
                onClick={() => onDayClick(cell.date)}
              >
                <div style={styles.monthDayHeader}>
                  <span style={{ ...styles.monthDayNumber, ...(isToday ? styles.monthDayNumberToday : {}) }}>{parseISO(cell.date).getDate()}</span>
                  {isElectionDay && <span style={styles.electionDayTag}>E-DAY</span>}
                </div>
                <div style={styles.monthDayPills}>
                  {visible.map((t) => <DayPill key={t.id} task={t} onEditTask={onEditTask} />)}
                  {extra > 0 && <button style={styles.monthDayMore} onClick={(e) => { e.stopPropagation(); onDayClick(cell.date); }}>+{extra} more</button>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function WeekGrid({ selectedDate, tasksByDate, today, activeElection, onDayClick, onEditTask }) {
  const start = startOfWeekISO(selectedDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div style={styles.weekGrid}>
      {days.map((d) => {
        const dayTasks = tasksByDate[d] || [];
        const isElectionDay = d === activeElection.date;
        const isToday = d === today;
        const dt = parseISO(d);
        return (
          <div key={d} style={{ ...styles.weekDayCol, background: isElectionDay ? "#fbf4de" : CARD }}>
            <div style={styles.weekDayHeader} onClick={() => onDayClick(d)}>
              <div style={styles.weekDayHeaderTop}>{WEEKDAY_SHORT[dt.getDay()]}</div>
              <div style={{ ...styles.weekDayHeaderNum, ...(isToday ? styles.monthDayNumberToday : {}) }}>{dt.getDate()}</div>
              {isElectionDay && <span style={styles.electionDayTag}>E-DAY</span>}
            </div>
            <div style={styles.weekDayPills}>
              {dayTasks.length === 0 && <div style={styles.weekDayEmpty}>—</div>}
              {dayTasks.slice(0, 5).map((t) => <DayPill key={t.id} task={t} onEditTask={onEditTask} />)}
              {dayTasks.length > 5 && (
                <button style={styles.weekDayMore} onClick={() => onDayClick(d)}>+{dayTasks.length - 5} more</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayList({ selectedDate, tasks, today, activeElection, onEditTask, onDuplicateTask, onDeleteTask, onStatus, onViewHistory }) {
  const isElectionDay = selectedDate === activeElection.date;
  return (
    <div>
      {isElectionDay && (
        <div style={styles.dayElectionBanner}><Vote size={15} color={GOLD} /> This is Election Day for {activeElection.name}.</div>
      )}
      {tasks.length === 0 ? (
        <div style={styles.emptyState}>
          <CalendarDays size={26} color={GOLD} />
          <div style={styles.emptyTitle}>Nothing due this day</div>
          <div style={styles.emptyBody}>No tasks land on {formatDayHeader(selectedDate)}.</div>
        </div>
      ) : (
        <div style={styles.taskList}>
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} today={today} onEdit={() => onEditTask(t)} onDuplicate={() => onDuplicateTask(t)} onDelete={() => onDeleteTask(t)} onStatus={(s) => onStatus(t, s)} onViewHistory={onViewHistory} />
          ))}
        </div>
      )}
    </div>
  );
}

function GanttView({ tasks, activeElection, today, onEditTask }) {
  const sorted = useMemo(() => [...tasks].sort((a, b) => dayDiff(a.dueDate, b.dueDate)), [tasks]);

  const { rangeStart, totalDays } = useMemo(() => {
    const allDates = [today, activeElection.date, ...sorted.map((t) => t.dueDate)];
    let min = allDates[0], max = allDates[0];
    allDates.forEach((d) => {
      if (dayDiff(d, min) < 0) min = d;
      if (dayDiff(d, max) > 0) max = d;
    });
    const start = addDays(min, -5);
    const end = addDays(max, 5);
    return { rangeStart: start, totalDays: Math.max(dayDiff(end, start), 14) };
  }, [sorted, today, activeElection.date]);

  function pct(d) {
    return Math.min(100, Math.max(0, (dayDiff(d, rangeStart) / totalDays) * 100));
  }
  const todayPct = pct(today);
  const electionPct = pct(activeElection.date);

  return (
    <div style={styles.ganttWrap}>
      <div style={styles.ganttLegend}>
        <span style={styles.ganttLegendItem}><span style={{ ...styles.ganttLegendDot, background: MUTED }} /> Not started</span>
        <span style={styles.ganttLegendItem}><span style={{ ...styles.ganttLegendDot, background: GOLD }} /> In progress</span>
        <span style={styles.ganttLegendItem}><span style={{ ...styles.ganttLegendDot, background: RED }} /> Blocked / overdue</span>
        <span style={styles.ganttLegendItem}><span style={{ ...styles.ganttLegendDot, background: NAVY }} /> Completed</span>
      </div>
      <div style={styles.ganttChart}>
        <div style={{ ...styles.ganttGridLine, left: `${todayPct}%` }}>
          <span style={styles.ganttGridLabel}>Today</span>
        </div>
        <div style={{ ...styles.ganttGridLine, left: `${electionPct}%`, borderColor: GOLD }}>
          <span style={{ ...styles.ganttGridLabel, color: GOLD }}>E-Day</span>
        </div>
        {sorted.map((t) => {
          const cat = catInfo(t.category);
          const isOverdue = t.status !== "completed" && dayDiff(t.dueDate, today) < 0;
          let barLeft, barWidth, barColor;
          if (t.status === "completed") {
            barLeft = pct(t.dueDate);
            barWidth = 0.8;
            barColor = NAVY;
          } else if (isOverdue) {
            barLeft = pct(t.dueDate);
            barWidth = Math.max(pct(today) - pct(t.dueDate), 0.8);
            barColor = RED;
          } else {
            barLeft = pct(today);
            barWidth = Math.max(pct(t.dueDate) - pct(today), 0.8);
            barColor = t.status === "in_progress" ? GOLD : t.status === "blocked" ? RED : MUTED;
          }
          return (
            <div key={t.id} style={styles.ganttRow}>
              <div style={styles.ganttLabelCol}>
                <span style={{ ...styles.milestoneBadge, borderColor: isOverdue ? RED : NAVY, color: isOverdue ? RED : NAVY, fontSize: 11, padding: "1px 5px", minWidth: 40 }}>{t.label}</span>
                <span style={styles.ganttTaskTitle} title={t.title}>{t.title}</span>
              </div>
              <div style={styles.ganttTrack}>
                <button
                  style={{ ...styles.ganttBar, left: `${barLeft}%`, width: `${barWidth}%`, background: barColor }}
                  onClick={() => onEditTask(t)}
                  title={`${t.title} — due ${formatNiceDate(t.dueDate)}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssigneeEditor({ assignees, setAssignees, people, onEnsurePerson }) {
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [pickValue, setPickValue] = useState("");

  const assignedPersonIds = new Set(assignees.map((a) => a.personId).filter(Boolean));
  const pickable = people.filter((p) => !assignedPersonIds.has(p.id)).sort((a, b) => a.name.localeCompare(b.name));

  function addAssignee() {
    if (!nameInput.trim() && !emailInput.trim()) return;
    const person = onEnsurePerson(nameInput, emailInput);
    setAssignees([...assignees, { id: uid(), personId: person.id, name: person.name, email: person.email }]);
    setNameInput("");
    setEmailInput("");
  }
  function addFromPickList(personId) {
    if (!personId) return;
    const person = people.find((p) => p.id === personId);
    if (!person) return;
    setAssignees([...assignees, { id: uid(), personId: person.id, name: person.name, email: person.email }]);
    setPickValue("");
  }
  function removeAssignee(id) {
    setAssignees(assignees.filter((a) => a.id !== id));
  }
  function handleKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addAssignee();
    }
  }

  return (
    <div style={styles.milestoneBox}>
      <div style={styles.fieldLabelText}>Responsible {assignees.length > 1 ? "parties" : "party"}</div>
      {assignees.length > 0 && (
        <div style={styles.assigneeList}>
          {assignees.map((a) => (
            <div key={a.id} style={styles.assigneeRow}>
              <span style={styles.assigneeRowText}>{a.name || "(no name)"}{a.email ? ` · ${a.email}` : ""}</span>
              <button style={styles.iconBtn} onClick={() => removeAssignee(a.id)} aria-label="Remove person"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {pickable.length > 0 && (
        <select style={{ ...styles.select, width: "100%", marginBottom: 8 }} value={pickValue} onChange={(e) => addFromPickList(e.target.value)}>
          <option value="">+ Add from pick list…</option>
          {pickable.map((p) => (
            <option key={p.id} value={p.id}>{p.name || p.email}{p.email && p.name ? ` (${p.email})` : ""}</option>
          ))}
        </select>
      )}
      <div style={styles.milestoneRow}>
        {/* minWidth 0: an input's built-in width (about 20 characters) otherwise stops it
            shrinking, which pushed "+ Add" past the dialog's edge (Fred, 2026-11-02). */}
        <input
          style={{ ...styles.textInput, flex: 1, minWidth: 0 }}
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Or type a new name"
          list="people-picklist-datalist"
        />
        <datalist id="people-picklist-datalist">
          {people.map((p) => <option key={p.id} value={p.name} />)}
        </datalist>
        <input
          type="email"
          style={{ ...styles.textInput, flex: 1, minWidth: 0 }}
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Email (optional)"
        />
        <button type="button" style={styles.addAssigneeBtn} onClick={addAssignee}><Plus size={14} /> Add</button>
      </div>
      <div style={styles.pickListHint}>New names are added to the pick list automatically for next time.</div>
    </div>
  );
}

function TaskModal({ draft, setDraft, electionDate, people, onEnsurePerson, onCancel, onSave }) {
  const previewDate = milestoneToDate(electionDate, { unit: draft.milestoneUnit, amount: Number(draft.milestoneAmount) || 0, side: draft.milestoneSide });
  const previewLabel = milestoneLabel({ unit: draft.milestoneUnit, amount: Number(draft.milestoneAmount) || 0, side: draft.milestoneSide });
  function set(field, value) { setDraft((d) => ({ ...d, [field]: value })); }

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span>{draft.id ? "Edit Task" : "Add Task"}</span>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <label style={styles.fieldLabel}>
            Task title
            <input style={styles.textInput} value={draft.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Finalize ballot layout" autoFocus />
          </label>
          <label style={styles.fieldLabel}>
            Description
            <textarea style={{ ...styles.textInput, minHeight: 60, resize: "vertical" }} value={draft.description} onChange={(e) => set("description", e.target.value)} placeholder="Optional details" />
          </label>
          <div style={styles.fieldRow}>
            <label style={styles.fieldLabel}>
              Category
              <select style={styles.select} value={draft.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label style={styles.fieldLabel}>
              Status
              <select style={styles.select} value={draft.status} onChange={(e) => set("status", e.target.value)}>
                {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          </div>
          <div style={styles.milestoneBox}>
            <div style={styles.fieldLabelText}>Countdown milestone</div>
            <div style={styles.milestoneRow}>
              <select style={styles.select} value={draft.milestoneSide} onChange={(e) => set("milestoneSide", e.target.value)}>
                <option value="before">Before E-Day</option>
                <option value="after">After E-Day</option>
              </select>
              <input type="number" min="0" style={{ ...styles.textInput, width: 80 }} value={draft.milestoneAmount} onChange={(e) => set("milestoneAmount", e.target.value)} />
              <select style={styles.select} value={draft.milestoneUnit} onChange={(e) => set("milestoneUnit", e.target.value)}>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
            </div>
            <div style={styles.milestonePreview}>
              <span style={styles.milestonePreviewBadge}>{previewLabel}</span>
              due {formatNiceDate(previewDate)}
            </div>
          </div>
          <AssigneeEditor assignees={draft.assignees} setAssignees={(next) => set("assignees", next)} people={people} onEnsurePerson={onEnsurePerson} />
          <label style={styles.fieldLabel}>
            Document link or reference
            <input style={styles.textInput} value={draft.docRef} onChange={(e) => set("docRef", e.target.value)} placeholder="Paste a Google Drive link, or note where files live" />
          </label>
          {draft.id && draft.history && draft.history.length > 0 && <TaskHistory history={draft.history} />}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.primaryBtn} onClick={onSave} disabled={!draft.title.trim()}><CheckCircle2 size={16} /> {draft.id ? "Save Changes" : "Add Task"}</button>
        </div>
      </div>
    </div>
  );
}

function TaskHistoryModal({ task, onClose }) {
  const sorted = [...(task.history || [])].sort((a, b) => new Date(b.at) - new Date(a.at));
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span><History size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} /> Activity — {task.title}</span>
          <button style={styles.iconBtn} onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.viewOnlyNote}>View only — activity history can't be edited or deleted, by anyone.</div>
          <div style={styles.historyList}>
            {sorted.map((h) => (
              <div key={h.id} style={styles.historyRow}>
                <span style={styles.historyText}>{h.text}</span>
                <span style={styles.historyMeta}>{formatDateTime(h.at)} · {h.by}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function TaskHistory({ history }) {
  const [open, setOpen] = useState(false);
  const sorted = [...history].sort((a, b) => new Date(b.at) - new Date(a.at));
  const visible = open ? sorted : sorted.slice(0, 2);
  return (
    <div style={styles.historyBox}>
      <div style={styles.historyHeader}><History size={13} color={MUTED} /> Activity</div>
      <div style={styles.historyList}>
        {visible.map((h) => (
          <div key={h.id} style={styles.historyRow}>
            <span style={styles.historyText}>{h.text}</span>
            <span style={styles.historyMeta}>{formatDateTime(h.at)} · {h.by}</span>
          </div>
        ))}
      </div>
      {sorted.length > 2 && (
        <button style={styles.showMoreBtn} onClick={() => setOpen((v) => !v)}>
          {open ? "Show less" : `Show all ${sorted.length} entries`}
        </button>
      )}
    </div>
  );
}

function ElectionModal({ draft, setDraft, onCancel, onSave }) {
  function set(field, value) { setDraft((d) => ({ ...d, [field]: value })); }
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span>{draft.id ? "Edit Election" : "Add Election"}</span>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <label style={styles.fieldLabel}>
            Election name
            <input style={styles.textInput} value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. November 2026 General Election" autoFocus />
          </label>
          <label style={styles.fieldLabel}>
            Election date
            <input type="date" style={styles.textInput} value={draft.date} onChange={(e) => set("date", e.target.value)} />
          </label>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.primaryBtn} onClick={onSave} disabled={!draft.name.trim() || !draft.date}><CheckCircle2 size={16} /> {draft.id ? "Save Changes" : "Add Election"}</button>
        </div>
      </div>
    </div>
  );
}

function CopyTasksModal({ elections, tasks, activeElectionName, onCancel, onCopy }) {
  const [sourceId, setSourceId] = useState(elections[0] ? elections[0].id : "");
  const [resetStatus, setResetStatus] = useState(true);
  const sourceCount = tasks.filter((t) => t.electionId === sourceId).length;

  if (elections.length === 0) {
    return (
      <div style={styles.modalOverlay} onClick={onCancel}>
        <div style={{ ...styles.modal, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalBody}>
            <div style={{ fontFamily: BODY_FONT, fontWeight: 700, fontSize: 19, color: INK, marginBottom: 8 }}>No other elections yet</div>
            <div style={{ color: MUTED, fontSize: 14 }}>Add another election first, then you can copy its task list over.</div>
          </div>
          <div style={styles.modalFooter}>
            <button style={styles.secondaryBtn} onClick={onCancel}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span>Copy Tasks</span>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <div style={{ fontSize: 13.5, color: MUTED }}>
            Copy a task list into <strong style={{ color: INK }}>{activeElectionName}</strong>. Each task keeps its E-day offset, so due dates recalculate for this election's date automatically.
          </div>
          <label style={styles.fieldLabel}>
            Copy tasks from
            <select style={styles.select} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              {elections.map((el) => (
                <option key={el.id} value={el.id}>{el.name} — {formatNiceDate(el.date)}</option>
              ))}
            </select>
          </label>
          <div style={styles.copyCountNote}>{sourceCount} task{sourceCount === 1 ? "" : "s"} will be copied.</div>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={resetStatus} onChange={(e) => setResetStatus(e.target.checked)} />
            Reset copied tasks to "Not Started"
          </label>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.primaryBtn} onClick={() => onCopy(sourceId, resetStatus)} disabled={sourceCount === 0}>
            <Copy size={16} /> Copy {sourceCount || ""} Task{sourceCount === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminScreen({ people, tasks, elections, countTasksForPerson, tasksForPerson, orphanedTasks, auditEntries, onReviewTask, onAddPerson, onRemovePerson, onDeleteTask, onDeleteElection, onBack, onExportTable, backup, confirm }) {
  const [auditPersonFilter, setAuditPersonFilter] = useState("all");
  const [auditElectionFilter, setAuditElectionFilter] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");
  const [auditLimit, setAuditLimit] = useState(25);
  const [deleteElectionTarget, setDeleteElectionTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const sortedPeople = [...people].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  const sortedElections = [...elections].sort((a, b) => dayDiff(a.date, b.date));
  // Download names start with the office, so files from two offices never mix: "anytown-people.xlsx".
  const office = fileSlug(backup.orgId);
  const exportAs = (what, sheetName, rows) => (format) => onExportTable(format, `${office}-${what}`, sheetName, rows());

  const auditQuery = auditSearch.trim().toLowerCase();
  const filteredAudit = auditEntries.filter((e) => {
    if (auditPersonFilter !== "all" && e.by !== auditPersonFilter) return false;
    if (auditElectionFilter === "__none__" && e.election) return false;
    if (auditElectionFilter !== "all" && auditElectionFilter !== "__none__" && e.election?.id !== auditElectionFilter) return false;
    if (auditQuery && !e.text.toLowerCase().includes(auditQuery) && !e.task.title.toLowerCase().includes(auditQuery) && !e.by.toLowerCase().includes(auditQuery)) return false;
    return true;
  });

  function handleAdd() {
    if (!addName.trim() && !addEmail.trim()) return;
    onAddPerson(addName, addEmail);
    setAddName("");
    setAddEmail("");
  }

  return (
    <div>
      <button style={styles.backBtn} onClick={onBack}><ArrowLeft size={15} /> Back to Tracker</button>
      <div style={styles.adminHeader}>
        <ShieldCheck size={22} color={GOLD} />
        <div>
          <div style={styles.adminTitle}>Admin</div>
          <div style={styles.adminSubtitle}>Back up your office's data, manage the people pick list, and review every change.</div>
        </div>
      </div>

      <BackupRestoreSection {...backup} />

      <section style={styles.adminSection}>
        <div style={styles.adminSectionHeader}><Users size={16} color={NAVY} /> Add a person to the pick list</div>
        <div style={styles.milestoneRow}>
          {/* 2.1.3: minWidth 0, as in 2.1.2's Add Task fix: without it the two boxes kept their built-in
              width and pushed "+ Add" 90px past the right edge of a 390px phone. */}
          <input style={{ ...styles.textInput, flex: 1, minWidth: 0 }} value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Name" />
          <input type="email" style={{ ...styles.textInput, flex: 1, minWidth: 0 }} value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="Email (optional)" />
          <button style={styles.addAssigneeBtn} onClick={handleAdd}><Plus size={14} /> Add</button>
        </div>
      </section>

      <section style={styles.adminSection}>
        <div style={styles.adminSectionHeader}><UserX size={16} color={RED} /> Clear out people who've left</div>
        <div style={styles.adminSectionBody}>
          Removing someone here takes them off the pick list. If they still have tasks assigned, you'll be asked whether to hand those tasks to someone else or leave them unassigned. Task counts are across <strong>all elections</strong>, not just the one you're currently viewing — click a person to see exactly which tasks and which elections.
        </div>
        <div style={styles.exportRow}>
          <ExportButtons what="the people list" disabled={sortedPeople.length === 0} onExport={exportAs("people", "People", () => peopleRows(sortedPeople, countTasksForPerson))} />
        </div>
        {sortedPeople.length === 0 ? (
          <div style={styles.emptyBody}>No one is on the pick list yet. Add responsible parties from any task, or add them here directly.</div>
        ) : (
          <div style={styles.adminPeopleList}>
            {sortedPeople.map((p) => {
              const count = countTasksForPerson(p.id);
              const isExpanded = expandedId === p.id;
              return (
                <div key={p.id} style={styles.adminPersonCard}>
                  <div style={styles.adminPersonRow}>
                    <button style={styles.adminPersonMain} onClick={() => setExpandedId(isExpanded ? null : p.id)} disabled={count === 0}>
                      <div>
                        <div style={styles.adminPersonName}>{p.name || "(no name)"}</div>
                        <div style={styles.adminPersonMeta}>
                          {p.email || "No email on file"} · {count} task{count === 1 ? "" : "s"} assigned
                          {count > 0 && <span style={styles.adminExpandHint}>{isExpanded ? " · Hide" : " · Show tasks"}</span>}
                        </div>
                      </div>
                    </button>
                    <button style={styles.secondaryBtn} onClick={() => setRemoveTarget(p)}>Remove</button>
                  </div>
                  {isExpanded && count > 0 && (
                    <div style={styles.adminTaskBreakdown}>
                      {tasksForPerson(p.id).map(({ task, election }) => (
                        <div key={task.id} style={styles.adminTaskBreakdownRow}>
                          <span style={styles.adminTaskBreakdownTitle}>{task.title}</span>
                          <span style={styles.adminTaskBreakdownMeta}>{election ? election.name : "Unknown election"} · {STATUSES.find((s) => s.id === task.status)?.label || task.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {orphanedTasks.length > 0 && (
        <section style={styles.adminSection}>
          <div style={styles.adminSectionHeader}><AlertTriangle size={16} color={RED} /> Legacy tasks (not linked to any election)</div>
          <div style={styles.adminSectionBody}>
            These tasks reference an election that no longer exists — usually left over from before an election was deleted, or from data created before this app tracked multiple elections. They won't show up anywhere in the regular task list. Delete the ones you don't need.
          </div>
          <div style={styles.exportRow}>
            <ExportButtons what="these legacy tasks" onExport={exportAs("legacy-tasks", "Legacy tasks", () => taskRows(orphanedTasks, (id) => catInfo(id).label))} />
          </div>
          <div style={styles.adminPeopleList}>
            {orphanedTasks.map((t) => (
              <div key={t.id} style={styles.adminPersonRow}>
                <div>
                  <div style={styles.adminPersonName}>{t.title}</div>
                  <div style={styles.adminPersonMeta}>
                    {(t.assignees || []).map((a) => a.name || a.email).filter(Boolean).join(", ") || "Unassigned"} · {STATUSES.find((s) => s.id === t.status)?.label || t.status}
                  </div>
                </div>
                <button style={{ ...styles.secondaryBtn, color: RED, borderColor: RED }} onClick={() => onDeleteTask(t)}>Delete</button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={styles.adminSection}>
        <div style={styles.adminSectionHeader}><History size={16} color={NAVY} /> Audit trail — all activity</div>
        <div style={styles.adminSectionBody}>
          Every change, across every election, in one place — who made it, when, and on which task. Click a row to jump straight to that task. The download holds every row that matches the filters, not just the ones shown.
        </div>
        <div style={styles.auditFilterRow}>
          <div style={styles.searchWrap}>
            <Search size={14} color={MUTED} />
            <input style={styles.searchInput} placeholder="Search activity or task…" value={auditSearch} onChange={(e) => { setAuditSearch(e.target.value); setAuditLimit(25); }} />
          </div>
          <select style={styles.select} value={auditPersonFilter} onChange={(e) => { setAuditPersonFilter(e.target.value); setAuditLimit(25); }}>
            <option value="all">Everyone</option>
            {[...people].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)).map((p) => (
              <option key={p.id} value={p.name}>{p.name || p.email}</option>
            ))}
          </select>
          <select style={styles.select} value={auditElectionFilter} onChange={(e) => { setAuditElectionFilter(e.target.value); setAuditLimit(25); }}>
            <option value="all">All elections</option>
            {elections.map((el) => <option key={el.id} value={el.id}>{el.name}</option>)}
            <option value="__none__">No election (legacy)</option>
          </select>
          <ExportButtons what="the matching activity" disabled={filteredAudit.length === 0} onExport={exportAs("audit-trail", "Audit trail", () => auditRows(filteredAudit))} />
        </div>
        {(() => {
          const filtered = filteredAudit;
          if (filtered.length === 0) {
            return <div style={styles.emptyBody}>No activity matches these filters.</div>;
          }
          const visible = filtered.slice(0, auditLimit);
          return (
            <>
              <div style={styles.auditList}>
                {visible.map((e) => (
                  <button key={e.id} style={styles.auditRow} onClick={() => onReviewTask(e.task)}>
                    <div style={styles.auditRowMain}>
                      <span style={styles.auditRowText}>{e.text}</span>
                      <span style={styles.auditRowTask}>on "{e.task.title}"</span>
                    </div>
                    <div style={styles.auditRowMeta}>
                      <span>{formatDateTime(e.at)}</span>
                      <span>·</span>
                      <span>{e.by}</span>
                      <span>·</span>
                      <span style={{ color: e.election ? MUTED : RED }}>{e.election ? e.election.name : "No election"}</span>
                    </div>
                  </button>
                ))}
              </div>
              {filtered.length > auditLimit && (
                <button style={styles.showMoreBtn} onClick={() => setAuditLimit((n) => n + 25)}>
                  Show {Math.min(25, filtered.length - auditLimit)} more (of {filtered.length} total)
                </button>
              )}
            </>
          );
        })()}
      </section>

      <section style={styles.adminSection}>
        <div style={styles.adminSectionHeader}><Vote size={16} color={RED} /> Delete an election</div>
        <div style={styles.adminSectionBody}>
          This permanently removes an election and every task in it — for everyone using this tracker. There's no undo, so this asks you to confirm three separate times before anything is deleted. It also needs a confirmed sign-in (below, if yours isn't).
        </div>
        {!confirm.confirmed && <ConfirmSigninNote help={confirm.help} />}
        <div style={styles.exportRow}>
          <ExportButtons what="the elections list" disabled={elections.length === 0} onExport={exportAs("elections", "Elections", () => electionRows(sortedElections, tasks))} />
        </div>
        {elections.length === 0 ? (
          <div style={styles.emptyBody}>No elections set up yet.</div>
        ) : (
          <div style={styles.adminPeopleList}>
            {sortedElections.map((el) => {
              const count = tasks.filter((t) => t.electionId === el.id).length;
              return (
                <div key={el.id} style={styles.adminPersonRow}>
                  <div>
                    <div style={styles.adminPersonName}>{el.name}</div>
                    <div style={styles.adminPersonMeta}>{formatNiceDate(el.date)} · {count} task{count === 1 ? "" : "s"}</div>
                  </div>
                  <button style={{ ...styles.secondaryBtn, color: RED, borderColor: RED, opacity: confirm.confirmed ? 1 : 0.5 }} disabled={!confirm.confirmed} onClick={() => setDeleteElectionTarget(el)}>Delete</button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {removeTarget && (
        <RemovePersonModal
          person={removeTarget}
          people={people}
          taskCount={countTasksForPerson(removeTarget.id)}
          onCancel={() => setRemoveTarget(null)}
          onConfirm={(reassignToId) => {
            onRemovePerson(removeTarget.id, reassignToId);
            setRemoveTarget(null);
          }}
        />
      )}

      {deleteElectionTarget && (
        <DeleteElectionModal
          election={deleteElectionTarget}
          taskCount={tasks.filter((t) => t.electionId === deleteElectionTarget.id).length}
          onCancel={() => setDeleteElectionTarget(null)}
          onConfirm={() => {
            onDeleteElection(deleteElectionTarget);
            setDeleteElectionTarget(null);
          }}
        />
      )}
    </div>
  );
}

function DeleteElectionModal({ election, taskCount, onCancel, onConfirm }) {
  const [step, setStep] = useState(1);
  const [typedName, setTypedName] = useState("");
  const nameMatches = typedName === election.name;

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span>Delete "{election.name}"</span>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>

        {step === 1 && (
          <>
            <div style={styles.modalBody}>
              <div style={styles.dangerBanner}>
                <AlertTriangle size={16} color={RED} />
                <div>
                  This permanently deletes <strong>{election.name}</strong> ({formatNiceDate(election.date)}) and all <strong>{taskCount} task{taskCount === 1 ? "" : "s"}</strong> in it — for everyone using this tracker. There is no undo.
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: MUTED }}>Check 1 of 3. If you're not sure, cancel and use "Copy Tasks From…" instead if you meant to reuse this election's tasks elsewhere.</div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
              <button style={{ ...styles.primaryBtn, background: RED }} onClick={() => setStep(2)}>I understand, continue</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={styles.modalBody}>
              <div style={{ fontSize: 12.5, color: MUTED }}>Check 2 of 3. To confirm, type the election's exact name below:</div>
              <div style={styles.typeToConfirmName}>{election.name}</div>
              <input
                style={styles.textInput}
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type the election name exactly"
                autoFocus
              />
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
              <button style={{ ...styles.primaryBtn, background: RED }} disabled={!nameMatches} onClick={() => setStep(3)}>Continue</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div style={styles.modalBody}>
              <div style={styles.dangerBanner}>
                <AlertTriangle size={16} color={RED} />
                <div>Check 3 of 3 — last chance. Click below to permanently delete <strong>{election.name}</strong> and its {taskCount} task{taskCount === 1 ? "" : "s"}.</div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
              <button style={{ ...styles.primaryBtn, background: RED }} onClick={onConfirm}><Trash2 size={16} /> Permanently Delete Election</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RemovePersonModal({ person, people, taskCount, onCancel, onConfirm }) {
  const [reassignTo, setReassignTo] = useState("");
  const others = people.filter((p) => p.id !== person.id).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span>Remove {person.name || person.email}</span>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          {taskCount > 0 ? (
            <>
              <div style={{ fontSize: 13.5, color: MUTED }}>
                {person.name || person.email} is currently assigned to <strong style={{ color: INK }}>{taskCount} task{taskCount === 1 ? "" : "s"}</strong> across your elections. What should happen to those tasks?
              </div>
              <label style={styles.fieldLabel}>
                Reassign their tasks to
                <select style={styles.select} value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                  <option value="">Leave those tasks unassigned</option>
                  {others.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
                </select>
              </label>
            </>
          ) : (
            <div style={{ fontSize: 13.5, color: MUTED }}>{person.name || person.email} has no tasks currently assigned. They'll be removed from the pick list.</div>
          )}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={{ ...styles.primaryBtn, background: RED }} onClick={() => onConfirm(reassignTo || null)}>
            <UserX size={16} /> Remove Person
          </button>
        </div>
      </div>
    </div>
  );
}

function DemoBanner({ demo, contactUrl, onBackup }) {
  if (demo.expired) {
    return (
      <div style={styles.errorBanner} role="alert">
        <AlertTriangle size={16} />
        <span style={{ flex: 1 }}>
          Your 30-day demo has ended{demo.endsAt ? ` (${formatDateTime(demo.endsAt)})` : ""}. Your tasks are still here to look at, but changes can't be saved. Download a backup to keep a copy, or contact us to extend your trial.
        </span>
        <button style={{ ...styles.retryBtn, display: "inline-flex", alignItems: "center", gap: 4 }} onClick={onBackup}><Download size={13} /> Download backup</button>
        {contactUrl && <a style={{ ...styles.retryBtn, marginLeft: 0, textDecoration: "none", background: NAVY }} href={contactUrl}>Contact us to extend your trial</a>}
      </div>
    );
  }
  return (
    <div style={styles.demoBanner}>
      <Clock size={15} />
      <span style={{ flex: 1 }}>
        Demo: {demo.daysLeft} day{demo.daysLeft === 1 ? "" : "s"} left of your 30-day trial. After that the tracker turns off, and you'll be able to download a backup of what you entered.
      </span>
      {contactUrl && <a style={styles.demoLink} href={contactUrl}>Contact us to extend your trial</a>}
    </div>
  );
}

// Same rule as the poll worker app: green within a day, yellow from 1 to 3 days, red after that
// or if nobody has ever taken one. Shown to owners and admins on every screen but Admin itself,
// which has the full Backup & Restore card.
function BackupBanner({ lastBackupAt, lastBackupBy, onBackUpNow }) {
  const { level, label, stale } = backupBannerLevel(lastBackupAt, lastBackupBy);
  const colors = {
    green: { bg: "#e5f3ec", fg: "#1d6b45", border: "#9fcdb3" },
    yellow: { bg: "#fbf1d6", fg: "#7a5a00", border: "#e5c96b" },
    red: { bg: "#fbe9eb", fg: RED, border: "#e8a1a9" },
  }[level];
  return (
    <div style={{ ...styles.backupBanner, background: colors.bg, color: colors.fg, borderColor: colors.border }} role="status">
      <span>{level === "green" ? "✓" : "⚠"} {label}</span>
      {stale && (
        <>
          <span>·</span>
          <button style={{ ...styles.linkBtn, color: colors.fg }} onClick={onBackUpNow}>Back up now</button>
        </>
      )}
    </div>
  );
}

// 2.1.3 (suite task 67, gap 5): what to do when restoring or deleting an election needs a confirmed
// sign-in. The wording and links come from the server (index.js confirmHelp), so a Poll Worker
// Management sign-in is only ever pointed back to Poll Worker Management.
function ConfirmSigninNote({ help }) {
  const h = help || { message: "Restoring a backup or deleting an election needs a confirmed sign-in. Sign out and sign in again, then try again." };
  return (
    <div style={styles.confirmNote} role="note" data-confirm-note="">
      <ShieldCheck size={17} color={NAVY} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div>{h.message}</div>
        {(h.actionUrl || h.securityUrl) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
            {h.actionUrl && <a style={{ ...styles.secondaryBtn, textDecoration: "none", display: "inline-block" }} href={h.actionUrl}>{h.actionLabel || "Sign in again"}</a>}
            {h.securityUrl && <a style={{ color: NAVY, fontSize: 13 }} href={h.securityUrl}>Security page</a>}
          </div>
        )}
      </div>
    </div>
  );
}

function BackupRestoreSection({ meta, onDownload, onRestore, orgId, orgName, readOnly, confirm }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [problem, setProblem] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileRef = useRef(null);

  async function take() {
    setBusy(true);
    try { await onDownload(); } finally { setBusy(false); }
  }

  async function choose(e) {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setParsed(null);
    setProblem("");
    if (!f) return;
    try {
      setParsed(await readBackupZip(f));
    } catch (err) {
      setProblem(err.message);
    }
  }

  async function runRestore(officeOk) {
    // This replaces everything, so it gets its own guard against a double click starting a second
    // restore while the first is still running.
    if (busy || !parsed) return;
    setBusy(true);
    try {
      const j = parsed.manifest.jurisdiction;
      await onRestore(parsed.collections, { backupOffice: j && typeof j.id === "string" ? j.id : null, confirmOffice: !!officeOk });
      setConfirmOpen(false);
      setFile(null);
      setParsed(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      // The server's office and sign-in refusals already say nothing was changed, and what to do.
      const own = err && err.body && (err.body.needsConfirm || err.body.officeCheck);
      setProblem(own ? err.message : `The restore didn't go through, and nothing was changed. ${err.message || ""}`);
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  const counts = parsed ? parsed.manifest.counts || { elections: parsed.collections.elections.length, tasks: parsed.collections.tasks.length, people: parsed.collections.people.length } : null;

  return (
    <section style={styles.adminSection} id="backup-restore">
      <div style={styles.adminSectionHeader}><Download size={16} color={NAVY} /> Backup &amp; Restore</div>
      <div style={styles.adminSectionBody}>
        Download everything in this tracker (every election, task, assignee, the people list and each task's change history) as a single file, or restore from one. You own your data, so keep your own copy: back up before and after every election and after any big change. We'll remind you when it's been a while. Each list here (tasks, elections, people, the audit trail) also downloads to Excel or CSV.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={styles.subHead}>Backup</div>
          <p style={styles.helpText}>Creates a .zip file containing every table as its own JSON file. Keep this somewhere safe — anyone with the file can restore it into this system.</p>
          <button style={{ ...styles.primaryBtn, background: GOLD, color: INK }} onClick={take} disabled={busy}><Download size={16} /> Download Full Backup (.zip)</button>
          <p style={{ ...styles.helpText, marginTop: 8 }}>
            {meta.lastBackupAt ? <>Last backup: <b>{formatDateTime(meta.lastBackupAt)}{meta.lastBackupBy ? ` by ${meta.lastBackupBy}` : ""}</b></> : <b>No backup has ever been taken.</b>} Shared across everyone in your office, not just you.
          </p>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={styles.subHead}>Restore</div>
          <p style={styles.helpText}><b>Warning:</b> restoring replaces all current data in this system with the contents of the backup file. This cannot be undone.</p>
          {readOnly ? (
            <p style={styles.helpText}>Restoring isn't available in a read-only office.</p>
          ) : (
            <>
              <p style={styles.helpText}>Restoring needs a confirmed sign-in, and a backup from another office (or an older one that doesn't say which office it's from) asks you to confirm that too.{confirm.confirmed && confirm.until ? <> Your sign-in is confirmed until <b>{formatTime(confirm.until)}</b>.</> : null}</p>
              {!confirm.confirmed && <ConfirmSigninNote help={confirm.help} />}
              <input ref={fileRef} type="file" accept=".zip,application/zip" onChange={choose} style={{ display: "block", marginBottom: 10 }} aria-label="Choose a backup file" />
              {parsed && (
                <div style={styles.backupSummary}>
                  Backup from <b>{parsed.manifest.savedAt ? formatDateTime(parsed.manifest.savedAt) : "an unknown date"}</b>{parsed.manifest.savedBy ? ` by ${parsed.manifest.savedBy}` : ""}
                  {parsed.manifest.jurisdiction ? ` for ${parsed.manifest.jurisdiction.name}` : ""}: {counts.elections} election{counts.elections === 1 ? "" : "s"}, {counts.tasks} task{counts.tasks === 1 ? "" : "s"}, {counts.people} {counts.people === 1 ? "person" : "people"}.
                </div>
              )}
              {problem && <div style={{ ...styles.errorBanner, marginBottom: 10 }}><AlertTriangle size={15} /> {problem}</div>}
              <button style={{ ...styles.primaryBtn, background: RED, opacity: !parsed || busy || !confirm.confirmed ? 0.5 : 1 }} disabled={!parsed || busy || !confirm.confirmed} onClick={() => setConfirmOpen(true)}><Upload size={16} /> Restore from Backup</button>
            </>
          )}
        </div>
      </div>
      {confirmOpen && parsed && (
        <RestoreConfirmModal
          parsed={parsed}
          counts={counts}
          fileName={file ? file.name : ""}
          otherOffice={parsed.manifest.jurisdiction && parsed.manifest.jurisdiction.id && parsed.manifest.jurisdiction.id !== orgId ? String(parsed.manifest.jurisdiction.name || parsed.manifest.jurisdiction.id) : null}
          unknownOffice={!(parsed.manifest.jurisdiction && parsed.manifest.jurisdiction.id)}
          orgName={orgName}
          busy={busy}
          onDownloadFirst={take}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={runRestore}
        />
      )}
    </section>
  );
}

function RestoreConfirmModal({ counts, fileName, otherOffice, unknownOffice, orgName, busy, onDownloadFirst, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  // 2.1.3 (suite task 67, gap 4): another office's backup, or one that doesn't say which office it
  // came from, needs this box ticked as well; the server refuses it otherwise.
  const [officeOk, setOfficeOk] = useState(false);
  const officeQuestion = !!otherOffice || !!unknownOffice;
  const ready = typed.trim().toUpperCase() === "RESTORE" && (!officeQuestion || officeOk);
  return (
    <div style={styles.modalOverlay} onClick={busy ? undefined : onCancel}>
      <div style={{ ...styles.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Restore from backup">
        <div style={styles.modalBody}>
          <div style={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 19, color: INK, marginBottom: 8 }}>Restore from backup?</div>
          <div style={styles.dangerBanner}>
            <AlertTriangle size={18} color={RED} style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 13.5, color: INK }}>
              This will replace <b>all</b> current elections, tasks, people and change history in this tracker, for everyone in your office, with the {counts.elections} election{counts.elections === 1 ? "" : "s"} and {counts.tasks} task{counts.tasks === 1 ? "" : "s"} in <b>{fileName || "this backup"}</b>. Anything added since that backup was taken will be gone. This cannot be undone.
            </div>
          </div>
          {otherOffice && (
            <div style={{ ...styles.dangerBanner, marginTop: 10 }}>
              <AlertTriangle size={18} color={RED} style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 13.5, color: INK }}>This backup came from a different office: <b>{otherOffice}</b>. Only continue if you mean to copy that office's data into yours.</div>
            </div>
          )}
          {!otherOffice && unknownOffice && (
            <div style={{ ...styles.dangerBanner, marginTop: 10 }}>
              <AlertTriangle size={18} color={RED} style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 13.5, color: INK }}>This backup doesn't say which office it came from (older backups didn't record it). Check that it is <b>{orgName}</b>'s before you continue.</div>
            </div>
          )}
          {officeQuestion && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13.5, color: INK, marginTop: 10 }}>
              <input type="checkbox" checked={officeOk} onChange={(e) => setOfficeOk(e.target.checked)} style={{ marginTop: 3 }} aria-label="Confirm the office this backup is restored into" />
              <span>{otherOffice ? <>Yes, replace <b>{orgName}</b>'s data with <b>{otherOffice}</b>'s backup.</> : <>Yes, this is <b>{orgName}</b>'s backup.</>}</span>
            </label>
          )}
          <p style={{ fontSize: 13.5, color: MUTED }}>Recommended: download a backup of what's here now first, so you can go back if this was the wrong file.</p>
          <button style={styles.secondaryBtn} onClick={onDownloadFirst} disabled={busy}><Download size={15} /> Download a backup of current data first</button>
          <label style={{ display: "block", fontSize: 13.5, color: INK, marginTop: 14 }}>
            Type <b>RESTORE</b> to confirm
            <input style={{ ...styles.textInput, marginTop: 6 }} value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus aria-label="Type RESTORE to confirm" />
          </label>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...styles.primaryBtn, background: RED, opacity: ready && !busy ? 1 : 0.5 }} disabled={!ready || busy} onClick={() => onConfirm(officeQuestion && officeOk)}>
            <Upload size={16} /> {busy ? "Restoring…" : "Replace everything"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StarterTasksModal({ electionName, electionDate, onCancel, onAdd }) {
  const [picked, setPicked] = useState(() => new Set(STARTER_TASKS.map((_, i) => i)));
  const toggle = (i) => {
    const next = new Set(picked);
    if (next.has(i)) next.delete(i); else next.add(i);
    setPicked(next);
  };
  const rows = STARTER_TASKS.map((t, i) => ({ ...t, i, due: milestoneToDate(electionDate, { unit: t.milestoneUnit, amount: t.milestoneAmount, side: t.milestoneSide }) }))
    .sort((a, b) => dayDiff(a.due, b.due));
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 620 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Starter task list">
        <div style={styles.modalHeader}>
          <div style={{ fontFamily: DISPLAY_FONT }}>Starter list for {electionName}</div>
          <button style={styles.iconBtn} onClick={onCancel} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <div style={{ ...styles.demoBanner, marginBottom: 12 }}>
            <AlertTriangle size={15} style={{ flexShrink: 0 }} />
            <span>These are common tasks with typical timing, not your state's legal deadlines. Check each date against your state's election calendar and edit them after adding.</span>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button style={styles.linkBtn} onClick={() => setPicked(new Set(STARTER_TASKS.map((_, i) => i)))}>Select all</button>
            <button style={styles.linkBtn} onClick={() => setPicked(new Set())}>Select none</button>
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto", border: `1px solid ${BORDER}`, borderRadius: 8 }}>
            {rows.map((t) => (
              <label key={t.i} style={styles.starterRow}>
                <input type="checkbox" checked={picked.has(t.i)} onChange={() => toggle(t.i)} />
                <span style={{ ...styles.starterTag, background: catInfo(t.category).color }}>{milestoneLabel({ unit: t.milestoneUnit, amount: t.milestoneAmount, side: t.milestoneSide })}</span>
                <span style={{ flex: 1 }}>{t.title}<span style={{ color: MUTED, fontSize: 12 }}> · {catInfo(t.category).label} · {formatNiceDate(t.due)}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.primaryBtn} disabled={picked.size === 0} onClick={() => onAdd(STARTER_TASKS.filter((_, i) => picked.has(i)))}>
            <Plus size={16} /> Add {picked.size} task{picked.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, body, onCancel, onConfirm }) {
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalBody}>
          <div style={{ fontFamily: BODY_FONT, fontWeight: 700, fontSize: 19, color: INK, marginBottom: 8 }}>{title}</div>
          <div style={{ color: MUTED, fontSize: 14 }}>{body}</div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
          <button style={{ ...styles.primaryBtn, background: RED }} onClick={onConfirm}><Trash2 size={16} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

const globalCss = `
input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; }
button:focus-visible, select:focus-visible, input:focus-visible, textarea:focus-visible, a:focus-visible { outline: 2px solid #2f6fd6; outline-offset: 2px; }
@media (max-width: 720px) { .ett-stats { grid-template-columns: repeat(2, 1fr) !important; } .ett-hero { flex-direction: column; align-items: flex-start !important; } .ett-header-inner { flex-wrap: wrap; gap: 10px; } .ett-header-right { gap: 8px !important; flex-wrap: wrap; order: 2; } .ett-header-apps { order: 1; margin-left: auto !important; } .ett-task-row { flex-wrap: wrap; } .ett-task-main { flex-basis: calc(100% - 70px) !important; } }
`;

const styles = {
  userChip: { display: "flex", alignItems: "center", gap: 5, color: "#fff", fontWeight: 600, fontSize: 12.5 },
  stripe: { height: 4, background: `linear-gradient(90deg, ${RED} 0 33%, #ffffff 33% 66%, ${RED} 66% 100%)` },
  demoBanner: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#fbf1d6", color: "#7a5a00", border: "1px solid #e5c96b", borderRadius: 8, padding: "10px 14px", fontSize: 13.5, marginBottom: 14 },
  demoLink: { color: "#7a5a00", fontWeight: 700, textDecoration: "underline" },
  sampleBanner: { display: "flex", alignItems: "center", gap: 8, background: "#e6eaf3", color: NAVY, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, marginBottom: 14 },
  noticeBanner: { display: "flex", alignItems: "center", gap: 8, background: "#e5f3ec", color: "#1d6b45", border: "1px solid #9fcdb3", borderRadius: 8, padding: "10px 14px", fontSize: 13.5, marginBottom: 14 },
  backupBanner: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", border: "1px solid", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, marginBottom: 14 },
  linkBtn: { background: "transparent", border: "none", padding: 0, textDecoration: "underline", cursor: "pointer", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13, color: NAVY },
  subHead: { fontWeight: 700, fontSize: 13, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 },
  helpText: { fontSize: 13, color: MUTED, margin: "0 0 10px" },
  confirmNote: { display: "flex", gap: 10, alignItems: "flex-start", background: "#eef2f9", border: "1px solid #c9d3e6", borderRadius: 8, padding: "10px 12px", margin: "0 0 12px", fontSize: 13.5, color: INK },
  backupSummary: { fontSize: 13, color: INK, background: PAPER, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "8px 10px", marginBottom: 10 },
  starterRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: `1px solid ${BORDER}`, fontSize: 13.5, cursor: "pointer" },
  starterTag: { color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 5, padding: "2px 6px", minWidth: 40, textAlign: "center" },
  page: { fontFamily: BODY_FONT, background: PAPER, minHeight: "100%", color: INK },
  header: { background: NAVY, borderBottom: `3px solid ${GOLD}` },
  headerInner: { maxWidth: 1000, margin: "0 auto", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  brandRow: { display: "flex", alignItems: "center", gap: 12 },
  brandTitle: { color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: "0.06em" },
  brandSub: { color: GOLD, fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 },
  saveIndicator: { fontSize: 12.5, minWidth: 50, textAlign: "right" },
  headerRight: { display: "flex", alignItems: "center", gap: 14, marginLeft: "auto" },
  headerApps: { display: "flex", alignItems: "center", marginLeft: 10, marginRight: -6 },
  adminBtn: { display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.1)", color: "#fff", border: `1px solid ${GOLD}`, borderRadius: 7, padding: "6px 12px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12.5, cursor: "pointer" },
  main: { maxWidth: 1000, margin: "0 auto", padding: "24px 20px 60px" },

  selectorRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 },
  electionSelectorWrap: { position: "relative" },
  copyTasksBtn: { display: "flex", alignItems: "center", gap: 6, background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: 8, padding: "8px 12px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  electionPickerBtn: { display: "flex", alignItems: "center", gap: 8, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "9px 14px", cursor: "pointer", fontFamily: BODY_FONT },
  electionPickerName: { fontWeight: 700, fontSize: 14, color: INK },
  electionPickerDate: { fontSize: 12.5, color: MUTED },
  electionDropdown: { position: "absolute", top: "calc(100% + 6px)", left: 0, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, boxShadow: "0 6px 20px rgba(24,33,58,0.12)", minWidth: 300, zIndex: 40, overflow: "hidden" },
  electionOption: { display: "flex", alignItems: "center", borderBottom: `1px solid ${BORDER}` },
  electionOptionMain: { flex: 1, textAlign: "left", background: "transparent", border: "none", padding: "10px 12px", cursor: "pointer", fontFamily: BODY_FONT },
  electionOptionName: { fontWeight: 700, fontSize: 13.5, color: INK },
  electionOptionDate: { fontSize: 12, color: MUTED, marginTop: 1 },
  electionAddOption: { display: "flex", alignItems: "center", gap: 6, width: "100%", background: PAPER, border: "none", padding: "10px 12px", cursor: "pointer", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13, color: NAVY },

  hero: { display: "flex", alignItems: "center", gap: 24, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "22px 26px", marginBottom: 18 },
  heroLeft: { flexShrink: 0 },
  seal: { width: 118, height: 118, borderRadius: "50%", border: `3px solid ${NAVY}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: `radial-gradient(circle at 50% 30%, #2c4170 0%, ${NAVY} 70%)`, position: "relative" },
  sealNumber: { color: "#fff", fontFamily: BODY_FONT, fontWeight: 900, fontSize: 32, lineHeight: 1, marginTop: 4 },
  sealLabel: { color: GOLD, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", marginTop: 2 },
  heroRight: { flex: 1 },
  heroEyebrow: { color: GOLD, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" },
  heroDate: { fontFamily: DISPLAY_FONT, fontWeight: 900, fontSize: 25, color: INK, marginTop: 2, marginBottom: 6 },
  heroHint: { fontSize: 12.5, color: MUTED },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 18 },
  statCard: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 10px", textAlign: "center" },
  statValue: { fontFamily: BODY_FONT, fontSize: 25, fontWeight: 900 },
  statLabel: { fontSize: 11, color: MUTED, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.03em" },

  toolbar: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 18 },

  viewToggleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  viewToggleGroup: { display: "flex", gap: 3, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: 3 },
  viewToggleBtn: { display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "none", borderRadius: 6, padding: "6px 11px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12.5, color: MUTED, cursor: "pointer" },
  viewToggleBtnActive: { background: NAVY, color: "#fff" },

  calNavRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  calNavLabel: { fontFamily: DISPLAY_FONT, fontWeight: 900, fontSize: 18, color: INK },
  calNavBtns: { display: "flex", alignItems: "center", gap: 6 },
  calNavBtn: { background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 7, padding: "6px 12px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12.5, color: NAVY, cursor: "pointer" },
  calNavIconBtn: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 7, padding: 6, display: "flex", color: INK, cursor: "pointer" },

  monthGrid: { border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 24 },
  monthWeekdayRow: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", background: NAVY },
  monthWeekdayCell: { textAlign: "center", padding: "7px 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "#fff" },
  monthWeekRow: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" },
  monthDayCell: { minHeight: 92, minWidth: 0, boxSizing: "border-box", borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: "6px 6px", display: "flex", flexDirection: "column", gap: 4 },
  monthDayHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  monthDayNumber: { fontSize: 12.5, fontWeight: 700, color: INK },
  monthDayNumberToday: { background: NAVY, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, flexShrink: 0 },
  electionDayTag: { fontSize: 8.5, fontWeight: 900, color: NAVY, background: "#fff", border: `1px solid ${GOLD}`, borderRadius: 4, padding: "1px 4px", whiteSpace: "nowrap", flexShrink: 0 },
  monthDayPills: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0, overflow: "hidden" },
  monthDayMore: { fontSize: 10.5, color: NAVY, fontWeight: 700, textAlign: "left", background: "transparent", border: "none", padding: "1px 3px", cursor: "pointer", textDecoration: "underline" },

  weekGrid: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 8, marginBottom: 24, alignItems: "start" },
  weekDayCol: { border: `1px solid ${BORDER}`, borderRadius: 8, minHeight: 220, minWidth: 0, boxSizing: "border-box", display: "flex", flexDirection: "column" },
  weekDayHeader: { padding: "8px 8px 6px", textAlign: "center", cursor: "pointer", borderBottom: `1px solid ${BORDER}` },
  weekDayHeaderTop: { fontSize: 10.5, fontWeight: 700, color: MUTED, letterSpacing: "0.04em" },
  weekDayHeaderNum: { fontSize: 15, fontWeight: 900, color: INK, marginTop: 2 },
  weekDayPills: { display: "flex", flexDirection: "column", gap: 4, padding: 6, flex: 1, minWidth: 0 },
  weekDayEmpty: { textAlign: "center", color: BORDER, fontSize: 12, marginTop: 8 },
  weekDayMore: { fontSize: 11, color: NAVY, fontWeight: 700, textAlign: "left", background: "transparent", border: "none", padding: "3px 6px", cursor: "pointer", textDecoration: "underline" },

  dayPill: { display: "flex", alignItems: "flex-start", gap: 5, background: PAPER, border: "none", borderRadius: 4, padding: "3px 6px", fontFamily: BODY_FONT, fontSize: 11, lineHeight: 1.35, color: INK, cursor: "pointer", textAlign: "left", width: "100%", minWidth: 0, boxSizing: "border-box", whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" },
  dayPillDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0, marginTop: 4 },

  dayElectionBanner: { display: "flex", alignItems: "center", gap: 8, background: "#fbf4de", border: `1px solid ${GOLD}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: INK, fontWeight: 700, marginBottom: 14 },

  ganttWrap: { marginBottom: 24 },
  ganttLegend: { display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 12 },
  ganttLegendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: MUTED },
  ganttLegendDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
  ganttChart: { position: "relative", border: `1px solid ${BORDER}`, borderRadius: 8, background: CARD, paddingTop: 22, paddingBottom: 8, marginTop: 10 },
  ganttGridLine: { position: "absolute", top: 0, bottom: 0, borderLeft: `1.5px dashed ${MUTED}`, zIndex: 1 },
  ganttGridLabel: { position: "absolute", top: -18, left: 3, fontSize: 9.5, fontWeight: 700, color: MUTED, whiteSpace: "nowrap" },
  ganttRow: { display: "flex", alignItems: "center", minHeight: 34, borderBottom: `1px solid ${BORDER}`, position: "relative" },
  ganttLabelCol: { width: 210, minWidth: 210, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 6, padding: "0 10px", flexShrink: 0 },
  ganttTaskTitle: { fontSize: 12, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  ganttTrack: { flex: 1, position: "relative", height: 34, minWidth: 0 },
  ganttBar: { position: "absolute", top: "50%", transform: "translateY(-50%)", height: 12, borderRadius: 6, border: "none", cursor: "pointer", zIndex: 2 },

  searchWrap: { display: "flex", alignItems: "center", gap: 6, background: CARD, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "7px 10px", flex: "1 1 180px" },
  searchInput: { border: "none", outline: "none", fontFamily: BODY_FONT, fontSize: 13.5, flex: 1, background: "transparent" },
  select: { fontFamily: BODY_FONT, fontSize: 13, padding: "7px 8px", borderRadius: 7, border: `1px solid ${BORDER}`, background: CARD, color: INK },
  checkboxLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: MUTED, whiteSpace: "nowrap" },

  primaryBtn: { display: "flex", alignItems: "center", gap: 6, background: NAVY, color: "#fff", border: "none", borderRadius: 7, padding: "9px 14px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
  secondaryBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "9px 14px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13.5, cursor: "pointer" },

  errorBanner: { display: "flex", alignItems: "center", gap: 8, background: "#fbe9eb", color: RED, border: `1px solid ${RED}`, borderRadius: 7, padding: "9px 12px", fontSize: 13, marginBottom: 14, flexWrap: "wrap" },
  dangerBanner: { display: "flex", alignItems: "flex-start", gap: 10, background: "#fbe9eb", border: `1px solid ${RED}`, borderRadius: 8, padding: "12px 14px", fontSize: 13, color: INK, lineHeight: 1.5 },
  typeToConfirmName: { fontFamily: BODY_FONT, fontWeight: 900, fontSize: 15, color: RED, background: "#fbe9eb", border: `1px dashed ${RED}`, borderRadius: 6, padding: "6px 10px", userSelect: "all" },
  retryBtn: { marginLeft: "auto", background: RED, color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },

  emptyState: { textAlign: "center", padding: "50px 20px", background: CARD, border: `1px dashed ${BORDER}`, borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  emptyTitle: { fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 18, color: INK },
  emptyBody: { color: MUTED, fontSize: 13.5, maxWidth: 380, marginBottom: 6 },

  group: { marginBottom: 20 },
  groupHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: BODY_FONT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 12.5, color: NAVY, borderBottom: `2px solid ${GOLD}`, paddingBottom: 6, marginBottom: 10 },
  groupCount: { fontFamily: BODY_FONT, fontStyle: "normal", fontSize: 12, color: MUTED, background: PAPER, borderRadius: 20, padding: "1px 9px" },
  taskList: { display: "flex", flexDirection: "column", gap: 8 },

  taskRow: { display: "flex", gap: 12, alignItems: "flex-start", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" },
  milestoneBadge: { fontFamily: BODY_FONT, fontWeight: 900, fontSize: 13, border: "1.5px solid", borderRadius: 6, padding: "3px 8px", minWidth: 52, textAlign: "center", flexShrink: 0 },
  taskMain: { flex: 1, minWidth: 0 },
  taskTitleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  taskTitle: { fontWeight: 700, fontSize: 14.5, overflowWrap: "anywhere", wordBreak: "break-word" },
  catTag: { fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20, letterSpacing: "0.02em" },
  taskDesc: { fontSize: 13, color: MUTED, marginTop: 3, overflowWrap: "anywhere", wordBreak: "break-word" },
  taskMeta: { display: "flex", flexWrap: "wrap", gap: 4, fontSize: 12, color: MUTED, marginTop: 5 },
  docRefWrap: { display: "inline-flex", alignItems: "center" },
  docLink: { display: "inline-flex", alignItems: "center", gap: 3, color: NAVY, textDecoration: "none", fontWeight: 600, overflowWrap: "anywhere", wordBreak: "break-word", maxWidth: 320 },
  showMoreBtn: { display: "inline", marginLeft: 6, background: "transparent", border: "none", padding: 0, color: NAVY, fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "underline" },
  emailLink: { display: "inline-flex", alignItems: "center", marginLeft: 4, color: NAVY, textDecoration: "none", verticalAlign: "middle" },
  assigneeWrap: { display: "inline" },
  assigneeItem: { display: "inline-flex", alignItems: "center" },
  taskActions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0 },
  statusPill: { fontFamily: BODY_FONT, fontSize: 11.5, fontWeight: 700, border: "1.3px solid", borderRadius: 20, padding: "4px 8px" },
  iconBtn: { background: "transparent", border: "none", color: MUTED, cursor: "pointer", padding: 5, display: "flex", borderRadius: 5 },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(24,33,58,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 },
  modal: { background: CARD, borderRadius: 10, width: "100%", maxWidth: 480, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${BORDER}`, fontFamily: BODY_FONT, fontWeight: 700, fontSize: 17, color: INK },
  modalBody: { padding: "16px 18px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 18px", borderTop: `1px solid ${BORDER}` },
  fieldLabel: { display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: MUTED, flex: 1 },
  fieldLabelText: { fontSize: 12.5, color: MUTED, marginBottom: 6 },
  fieldRow: { display: "flex", gap: 10 },
  copyCountNote: { fontSize: 12.5, color: NAVY, fontWeight: 700, marginTop: -4 },
  textInput: { fontFamily: BODY_FONT, fontSize: 13.5, padding: "8px 10px", borderRadius: 7, border: `1px solid ${BORDER}`, color: INK, outline: "none" },
  milestoneBox: { background: PAPER, borderRadius: 8, padding: "12px 14px" },
  milestoneRow: { display: "flex", gap: 8 },
  milestonePreview: { marginTop: 10, fontSize: 12.5, color: MUTED, display: "flex", alignItems: "center", gap: 8 },
  milestonePreviewBadge: { fontFamily: BODY_FONT, fontWeight: 900, color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 6, padding: "2px 7px" },
  assigneeList: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 },
  assigneeRow: { display: "flex", alignItems: "center", justifyContent: "space-between", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 8px" },
  assigneeRowText: { fontSize: 12.5, color: INK, overflowWrap: "anywhere" },
  addAssigneeBtn: { display: "flex", flexShrink: 0, alignItems: "center", gap: 4, background: NAVY, color: "#fff", border: "none", borderRadius: 7, padding: "8px 12px", fontFamily: BODY_FONT, fontWeight: 700, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" },
  pickListHint: { fontSize: 11, color: MUTED, marginTop: 8 },
  historyBox: { background: PAPER, borderRadius: 8, padding: "10px 14px" },
  viewOnlyNote: { fontSize: 11.5, color: MUTED, background: PAPER, borderRadius: 6, padding: "6px 10px", marginBottom: 4 },
  historyHeader: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: MUTED, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" },
  historyList: { display: "flex", flexDirection: "column", gap: 6 },
  historyRow: { display: "flex", flexDirection: "column", gap: 1 },
  historyText: { fontSize: 12.5, color: INK, overflowWrap: "anywhere" },
  historyMeta: { fontSize: 11, color: MUTED },
  backBtn: { display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: NAVY, fontFamily: BODY_FONT, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 16 },
  adminHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 22 },
  adminTitle: { fontFamily: DISPLAY_FONT, fontWeight: 900, fontSize: 22, color: INK },
  adminSubtitle: { fontSize: 13, color: MUTED, marginTop: 2 },
  adminSection: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", marginBottom: 16 },
  adminSectionHeader: { display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 14, color: INK, marginBottom: 10 },
  adminSectionBody: { fontSize: 12.5, color: MUTED, marginBottom: 14, lineHeight: 1.5 },
  adminPeopleList: { display: "flex", flexDirection: "column", gap: 8 },
  adminPersonCard: { border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" },
  adminPersonRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", gap: 10 },
  adminPersonMain: { flex: 1, textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", minWidth: 0 },
  adminPersonName: { fontWeight: 700, fontSize: 13.5, color: INK },
  adminPersonMeta: { fontSize: 12, color: MUTED, marginTop: 2 },
  adminExpandHint: { color: NAVY, fontWeight: 700 },
  adminTaskBreakdown: { borderTop: `1px solid ${BORDER}`, background: PAPER, display: "flex", flexDirection: "column" },
  adminTaskBreakdownRow: { display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${BORDER}`, fontSize: 12.5 },
  adminTaskBreakdownTitle: { color: INK, overflowWrap: "anywhere" },
  adminTaskBreakdownMeta: { color: MUTED, whiteSpace: "nowrap" },
  auditFilterRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  exportRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  auditList: { display: "flex", flexDirection: "column", gap: 6 },
  auditRow: { display: "flex", flexDirection: "column", gap: 3, textAlign: "left", background: CARD, border: `1px solid ${BORDER}`, borderRadius: 7, padding: "9px 12px", cursor: "pointer", width: "100%", fontFamily: BODY_FONT },
  auditRowMain: { display: "flex", flexWrap: "wrap", gap: 5, alignItems: "baseline" },
  auditRowText: { fontSize: 13, fontWeight: 700, color: INK },
  auditRowTask: { fontSize: 12.5, color: NAVY, overflowWrap: "anywhere" },
  auditRowMeta: { display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11.5, color: MUTED },
};
